/**
 * Centralized Location Data
 * Single source of truth for country and state/province data across all forms
 * 
 * Usage:
 * import { countryOptions, countryStateMap, getStateOptions, getFilteredCountryOptions, getFilteredStateOptions } from './locationData';
 */

// Comprehensive country and state/province mapping
export const countryStateMap = {
    "US": [
        { value: "AL", label: "Alabama" },
        { value: "AK", label: "Alaska" },
        { value: "AZ", label: "Arizona" },
        { value: "AR", label: "Arkansas" },
        { value: "CA", label: "California" },
        { value: "CO", label: "Colorado" },
        { value: "CT", label: "Connecticut" },
        { value: "DE", label: "Delaware" },
        { value: "DC", label: "District of Columbia" },
        { value: "FL", label: "Florida" },
        { value: "GA", label: "Georgia" },
        { value: "HI", label: "Hawaii" },
        { value: "ID", label: "Idaho" },
        { value: "IL", label: "Illinois" },
        { value: "IN", label: "Indiana" },
        { value: "IA", label: "Iowa" },
        { value: "KS", label: "Kansas" },
        { value: "KY", label: "Kentucky" },
        { value: "LA", label: "Louisiana" },
        { value: "ME", label: "Maine" },
        { value: "MD", label: "Maryland" },
        { value: "MA", label: "Massachusetts" },
        { value: "MI", label: "Michigan" },
        { value: "MN", label: "Minnesota" },
        { value: "MS", label: "Mississippi" },
        { value: "MO", label: "Missouri" },
        { value: "MT", label: "Montana" },
        { value: "NE", label: "Nebraska" },
        { value: "NV", label: "Nevada" },
        { value: "NH", label: "New Hampshire" },
        { value: "NJ", label: "New Jersey" },
        { value: "NM", label: "New Mexico" },
        { value: "NY", label: "New York" },
        { value: "NC", label: "North Carolina" },
        { value: "ND", label: "North Dakota" },
        { value: "OH", label: "Ohio" },
        { value: "OK", label: "Oklahoma" },
        { value: "OR", label: "Oregon" },
        { value: "PA", label: "Pennsylvania" },
        { value: "RI", label: "Rhode Island" },
        { value: "SC", label: "South Carolina" },
        { value: "SD", label: "South Dakota" },
        { value: "TN", label: "Tennessee" },
        { value: "TX", label: "Texas" },
        { value: "UT", label: "Utah" },
        { value: "VT", label: "Vermont" },
        { value: "VA", label: "Virginia" },
        { value: "WA", label: "Washington" },
        { value: "WV", label: "West Virginia" },
        { value: "WI", label: "Wisconsin" },
        { value: "WY", label: "Wyoming" },
    ],
    "CA": [
        { value: "AB", label: "Alberta" },
        { value: "BC", label: "British Columbia" },
        { value: "MB", label: "Manitoba" },
        { value: "NB", label: "New Brunswick" },
        { value: "NL", label: "Newfoundland and Labrador" },
        { value: "NS", label: "Nova Scotia" },
        { value: "ON", label: "Ontario" },
        { value: "PE", label: "Prince Edward Island" },
        { value: "QC", label: "Quebec" },
        { value: "SK", label: "Saskatchewan" },
        { value: "NT", label: "Northwest Territories" },
        { value: "NU", label: "Nunavut" },
        { value: "YT", label: "Yukon" },
    ],
    "AU": [
        { value: "ACT", label: "Australian Capital Territory" },
        { value: "NSW", label: "New South Wales" },
        { value: "NT", label: "Northern Territory" },
        { value: "QLD", label: "Queensland" },
        { value: "SA", label: "South Australia" },
        { value: "TAS", label: "Tasmania" },
        { value: "VIC", label: "Victoria" },
        { value: "WA", label: "Western Australia" },
    ],
    "GB": [
        { value: "ENG", label: "England" },
        { value: "SCT", label: "Scotland" },
        { value: "WLS", label: "Wales" },
        { value: "NIR", label: "Northern Ireland" },
        { value: "LND", label: "Greater London" }, // Special administrative region
    ],
    "IN": [
        { value: "AP", label: "Andhra Pradesh" },
        { value: "AR", label: "Arunachal Pradesh" },
        { value: "AS", label: "Assam" },
        { value: "BR", label: "Bihar" },
        { value: "CT", label: "Chhattisgarh" },
        { value: "DL", label: "Delhi" },
        { value: "DL-NCT", label: "National Capital Territory of Delhi" }, // Full official name
        { value: "DL-ALT", label: "NCT", canonicalName: "National Capital Territory of Delhi" }, // Abbreviation
        { value: "GA", label: "Goa" },
        { value: "GJ", label: "Gujarat" },
        { value: "HR", label: "Haryana" },
        { value: "HP", label: "Himachal Pradesh" },
        { value: "JK", label: "Jammu and Kashmir" },
        { value: "JH", label: "Jharkhand" },
        { value: "KA", label: "Karnataka" },
        { value: "KL", label: "Kerala" },
        { value: "MP", label: "Madhya Pradesh" },
        { value: "MH", label: "Maharashtra" },
        { value: "MN", label: "Manipur" },
        { value: "ML", label: "Meghalaya" },
        { value: "MZ", label: "Mizoram" },
        { value: "NL", label: "Nagaland" },
        { value: "OR", label: "Odisha" },
        { value: "PB", label: "Punjab" },
        { value: "RJ", label: "Rajasthan" },
        { value: "SK", label: "Sikkim" },
        { value: "TN", label: "Tamil Nadu" },
        { value: "TG", label: "Telangana" },
        { value: "TR", label: "Tripura" },
        { value: "UP", label: "Uttar Pradesh" },
        { value: "UT", label: "Uttarakhand" },
        { value: "WB", label: "West Bengal" },
    ],
    "BR": [
        { value: "AC", label: "Acre" },
        { value: "AL", label: "Alagoas" },
        { value: "AP", label: "Amapá" },
        { value: "AM", label: "Amazonas" },
        { value: "BA", label: "Bahia" },
        { value: "CE", label: "Ceará" },
        { value: "DF", label: "Distrito Federal" },
        { value: "ES", label: "Espírito Santo" },
        { value: "GO", label: "Goiás" },
        { value: "MA", label: "Maranhão" },
        { value: "MT", label: "Mato Grosso" },
        { value: "MS", label: "Mato Grosso do Sul" },
        { value: "MG", label: "Minas Gerais" },
        { value: "PA", label: "Pará" },
        { value: "PB", label: "Paraíba" },
        { value: "PR", label: "Paraná" },
        { value: "PE", label: "Pernambuco" },
        { value: "PI", label: "Piauí" },
        { value: "RJ", label: "Rio de Janeiro" },
        { value: "RN", label: "Rio Grande do Norte" },
        { value: "RS", label: "Rio Grande do Sul" },
        { value: "RO", label: "Rondônia" },
        { value: "RR", label: "Roraima" },
        { value: "SC", label: "Santa Catarina" },
        { value: "SP", label: "São Paulo" },
        { value: "SE", label: "Sergipe" },
        { value: "TO", label: "Tocantins" },
    ],
    "MX": [
        { value: "AG", label: "Aguascalientes" },
        { value: "BC", label: "Baja California" },
        { value: "BS", label: "Baja California Sur" },
        { value: "CM", label: "Campeche" },
        { value: "CS", label: "Chiapas" },
        { value: "CH", label: "Chihuahua" },
        { value: "CO", label: "Coahuila" },
        { value: "CL", label: "Colima" },
        { value: "DF", label: "Mexico City" },
        { value: "DG", label: "Durango" },
        { value: "GT", label: "Guanajuato" },
        { value: "GR", label: "Guerrero" },
        { value: "HG", label: "Hidalgo" },
        { value: "JA", label: "Jalisco" },
        { value: "EM", label: "Mexico State" },
        { value: "MI", label: "Michoacán" },
        { value: "MO", label: "Morelos" },
        { value: "NA", label: "Nayarit" },
        { value: "NL", label: "Nuevo León" },
        { value: "OA", label: "Oaxaca" },
        { value: "PU", label: "Puebla" },
        { value: "QT", label: "Querétaro" },
        { value: "QR", label: "Quintana Roo" },
        { value: "SL", label: "San Luis Potosí" },
        { value: "SI", label: "Sinaloa" },
        { value: "SO", label: "Sonora" },
        { value: "TB", label: "Tabasco" },
        { value: "TM", label: "Tamaulipas" },
        { value: "TL", label: "Tlaxcala" },
        { value: "VE", label: "Veracruz" },
        { value: "YU", label: "Yucatán" },
        { value: "ZA", label: "Zacatecas" },
    ],
    "DE": [
        { value: "BW", label: "Baden-Württemberg" },
        { value: "BY", label: "Bavaria" },
        { value: "BY-DE", label: "Bayern", canonicalName: "Bavaria" }, // German native name
        { value: "BE", label: "Berlin" },
        { value: "BB", label: "Brandenburg" },
        { value: "HB", label: "Bremen" },
        { value: "HH", label: "Hamburg" },
        { value: "HE", label: "Hesse" },
        { value: "MV", label: "Mecklenburg-Vorpommern" },
        { value: "NI", label: "Lower Saxony" },
        { value: "NI-DE", label: "Niedersachsen", canonicalName: "Lower Saxony" }, // German native name
        { value: "NW", label: "North Rhine-Westphalia" },
        { value: "NW-DE", label: "Nordrhein-Westfalen", canonicalName: "North Rhine-Westphalia" }, // German native name
        { value: "RP", label: "Rhineland-Palatinate" },
        { value: "RP-DE", label: "Rheinland-Pfalz", canonicalName: "Rhineland-Palatinate" }, // German native name
        { value: "SL", label: "Saarland" },
        { value: "SN", label: "Saxony" },
        { value: "ST", label: "Saxony-Anhalt" },
        { value: "SH", label: "Schleswig-Holstein" },
        { value: "TH", label: "Thuringia" },
    ],
    "AR": [
        { value: "BA", label: "Buenos Aires" },
        { value: "CT", label: "Catamarca" },
        { value: "CC", label: "Chaco" },
        { value: "CH", label: "Chubut" },
        { value: "CB", label: "Córdoba" },
        { value: "CR", label: "Corrientes" },
        { value: "ER", label: "Entre Ríos" },
        { value: "FO", label: "Formosa" },
        { value: "JY", label: "Jujuy" },
        { value: "LP", label: "La Pampa" },
        { value: "LR", label: "La Rioja" },
        { value: "MZ", label: "Mendoza" },
        { value: "MI", label: "Misiones" },
        { value: "NQ", label: "Neuquén" },
        { value: "RN", label: "Río Negro" },
        { value: "SA", label: "Salta" },
        { value: "SJ", label: "San Juan" },
        { value: "SL", label: "San Luis" },
        { value: "SC", label: "Santa Cruz" },
        { value: "SF", label: "Santa Fe" },
        { value: "SE", label: "Santiago del Estero" },
        { value: "TF", label: "Tierra del Fuego" },
        { value: "TU", label: "Tucumán" },
        { value: "CF", label: "Ciudad Autónoma de Buenos Aires" }
    ],
    "JP": [
        { value: "01", label: "Hokkaido" },
        { value: "02", label: "Aomori" },
        { value: "03", label: "Iwate" },
        { value: "04", label: "Miyagi" },
        { value: "05", label: "Akita" },
        { value: "06", label: "Yamagata" },
        { value: "07", label: "Fukushima" },
        { value: "08", label: "Ibaraki" },
        { value: "09", label: "Tochigi" },
        { value: "10", label: "Gunma" },
        { value: "11", label: "Saitama" },
        { value: "12", label: "Chiba" },
        { value: "13", label: "Tokyo" },
        { value: "13-PREF", label: "Tokyo Prefecture" }, // Full official name (Tokyo-to)
        { value: "14", label: "Kanagawa" },
        { value: "15", label: "Niigata" },
        { value: "16", label: "Toyama" },
        { value: "17", label: "Ishikawa" },
        { value: "18", label: "Fukui" },
        { value: "19", label: "Yamanashi" },
        { value: "20", label: "Nagano" },
        { value: "21", label: "Gifu" },
        { value: "22", label: "Shizuoka" },
        { value: "23", label: "Aichi" },
        { value: "24", label: "Mie" },
        { value: "25", label: "Shiga" },
        { value: "26", label: "Kyoto" },
        { value: "27", label: "Osaka" },
        { value: "28", label: "Hyogo" },
        { value: "29", label: "Nara" },
        { value: "30", label: "Wakayama" },
        { value: "31", label: "Tottori" },
        { value: "32", label: "Shimane" },
        { value: "33", label: "Okayama" },
        { value: "34", label: "Hiroshima" },
        { value: "35", label: "Yamaguchi" },
        { value: "36", label: "Tokushima" },
        { value: "37", label: "Kagawa" },
        { value: "38", label: "Ehime" },
        { value: "39", label: "Kochi" },
        { value: "40", label: "Fukuoka" },
        { value: "41", label: "Saga" },
        { value: "42", label: "Nagasaki" },
        { value: "43", label: "Kumamoto" },
        { value: "44", label: "Oita" },
        { value: "45", label: "Miyazaki" },
        { value: "46", label: "Kagoshima" },
        { value: "47", label: "Okinawa" }
    ],
    "ZA": [
        { value: "WC", label: "Western Cape" },
        { value: "EC", label: "Eastern Cape" },
        { value: "NC", label: "Northern Cape" },
        { value: "FS", label: "Free State" },
        { value: "KZN", label: "KwaZulu-Natal" },
        { value: "NW", label: "North West" },
        { value: "GT", label: "Gauteng" },
        { value: "MP", label: "Mpumalanga" },
        { value: "LP", label: "Limpopo" }
    ],
    "NG": [
        { value: "AB", label: "Abia" },
        { value: "AD", label: "Adamawa" },
        { value: "AK", label: "Akwa Ibom" },
        { value: "AN", label: "Anambra" },
        { value: "BA", label: "Bauchi" },
        { value: "BY", label: "Bayelsa" },
        { value: "BE", label: "Benue" },
        { value: "BO", label: "Borno" },
        { value: "CR", label: "Cross River" },
        { value: "DE", label: "Delta" },
        { value: "EB", label: "Ebonyi" },
        { value: "ED", label: "Edo" },
        { value: "EK", label: "Ekiti" },
        { value: "EN", label: "Enugu" },
        { value: "FC", label: "Federal Capital Territory" },
        { value: "FC-ABJ", label: "Abuja" }, // Capital city / FCT
        { value: "GO", label: "Gombe" },
        { value: "IM", label: "Imo" },
        { value: "JI", label: "Jigawa" },
        { value: "KD", label: "Kaduna" },
        { value: "KN", label: "Kano" },
        { value: "KT", label: "Katsina" },
        { value: "KE", label: "Kebbi" },
        { value: "KO", label: "Kogi" },
        { value: "KW", label: "Kwara" },
        { value: "LA", label: "Lagos" },
        { value: "NA", label: "Nasarawa" },
        { value: "NI", label: "Niger" },
        { value: "OG", label: "Ogun" },
        { value: "ON", label: "Ondo" },
        { value: "OS", label: "Osun" },
        { value: "OY", label: "Oyo" },
        { value: "PL", label: "Plateau" },
        { value: "RI", label: "Rivers" },
        { value: "SO", label: "Sokoto" },
        { value: "TA", label: "Taraba" },
        { value: "YO", label: "Yobe" },
        { value: "ZA", label: "Zamfara" }
    ],
    "CH": [
        { value: "AG", label: "Aargau" },
        { value: "AI", label: "Appenzell Innerrhoden" },
        { value: "AR", label: "Appenzell Ausserrhoden" },
        { value: "BE", label: "Bern" },
        { value: "BL", label: "Basel-Landschaft" },
        { value: "BS", label: "Basel-Stadt" },
        { value: "BS-EN", label: "Basel-City", canonicalName: "Basel-Stadt" }, // English
        { value: "BS-FR", label: "Bâle-Ville" }, // French
        { value: "FR", label: "Fribourg" },
        { value: "GE", label: "Geneva" },
        { value: "GL", label: "Glarus" },
        { value: "GR", label: "Graubünden" },
        { value: "JU", label: "Jura" },
        { value: "LU", label: "Lucerne" },
        { value: "NE", label: "Neuchâtel" },
        { value: "NW", label: "Nidwalden" },
        { value: "OW", label: "Obwalden" },
        { value: "SG", label: "St. Gallen" },
        { value: "SH", label: "Schaffhausen" },
        { value: "SO", label: "Solothurn" },
        { value: "SZ", label: "Schwyz" },
        { value: "TG", label: "Thurgau" },
        { value: "TI", label: "Ticino" },
        { value: "UR", label: "Uri" },
        { value: "VD", label: "Vaud" },
        { value: "VS", label: "Valais" },
        { value: "ZG", label: "Zug" },
        { value: "ZH", label: "Zürich" }
    ],
    "AT": [
        { value: "1", label: "Burgenland" },
        { value: "2", label: "Carinthia" },
        { value: "3", label: "Lower Austria" },
        { value: "4", label: "Upper Austria" },
        { value: "5", label: "Salzburg" },
        { value: "6", label: "Styria" },
        { value: "7", label: "Tyrol" },
        { value: "8", label: "Vorarlberg" },
        { value: "9", label: "Vienna" }
    ],
    "RU": [
        { value: "AD", label: "Adygea" },
        { value: "AL", label: "Altai Republic" },
        { value: "ALT", label: "Altai Krai" },
        { value: "AMU", label: "Amur Oblast" },
        { value: "ARK", label: "Arkhangelsk Oblast" },
        { value: "AST", label: "Astrakhan Oblast" },
        { value: "BA", label: "Bashkortostan" },
        { value: "BEL", label: "Belgorod Oblast" },
        { value: "BRY", label: "Bryansk Oblast" },
        { value: "BU", label: "Buryatia" },
        { value: "CE", label: "Chechen Republic" },
        { value: "CHE", label: "Chelyabinsk Oblast" },
        { value: "CHU", label: "Chukotka" },
        { value: "CU", label: "Chuvashia" },
        { value: "DA", label: "Dagestan" },
        { value: "IN", label: "Ingushetia" },
        { value: "IRK", label: "Irkutsk Oblast" },
        { value: "IVA", label: "Ivanovo Oblast" },
        { value: "KAM", label: "Kamchatka Krai" },
        { value: "KB", label: "Kabardino-Balkaria" },
        { value: "KC", label: "Karachay-Cherkessia" },
        { value: "KDA", label: "Krasnodar Krai" },
        { value: "KEM", label: "Kemerovo Oblast" },
        { value: "KGD", label: "Kaliningrad Oblast" },
        { value: "KGN", label: "Kurgan Oblast" },
        { value: "KHA", label: "Khabarovsk Krai" },
        { value: "KHM", label: "Khanty-Mansi" },
        { value: "KIR", label: "Kirov Oblast" },
        { value: "KK", label: "Khakassia" },
        { value: "KL", label: "Kalmykia" },
        { value: "KLU", label: "Kaluga Oblast" },
        { value: "KO", label: "Komi Republic" },
        { value: "KOS", label: "Kostroma Oblast" },
        { value: "KR", label: "Karelia" },
        { value: "KRS", label: "Kursk Oblast" },
        { value: "KYA", label: "Krasnoyarsk Krai" },
        { value: "LEN", label: "Leningrad Oblast" },
        { value: "LIP", label: "Lipetsk Oblast" },
        { value: "MAG", label: "Magadan Oblast" },
        { value: "ME", label: "Mari El" },
        { value: "MO", label: "Mordovia" },
        { value: "MOS", label: "Moscow Oblast" },
        { value: "MOW", label: "Moscow" },
        { value: "MUR", label: "Murmansk Oblast" },
        { value: "NEN", label: "Nenets" },
        { value: "NGR", label: "Novgorod Oblast" },
        { value: "NIZ", label: "Nizhny Novgorod Oblast" },
        { value: "NVS", label: "Novosibirsk Oblast" },
        { value: "OMS", label: "Omsk Oblast" },
        { value: "ORE", label: "Orenburg Oblast" },
        { value: "ORL", label: "Oryol Oblast" },
        { value: "PER", label: "Perm Krai" },
        { value: "PNZ", label: "Penza Oblast" },
        { value: "PRI", label: "Primorsky Krai" },
        { value: "PSK", label: "Pskov Oblast" },
        { value: "ROS", label: "Rostov Oblast" },
        { value: "RYA", label: "Ryazan Oblast" },
        { value: "SA", label: "Sakha Republic" },
        { value: "SAK", label: "Sakhalin Oblast" },
        { value: "SAM", label: "Samara Oblast" },
        { value: "SAR", label: "Saratov Oblast" },
        { value: "SE", label: "North Ossetia" },
        { value: "SMO", label: "Smolensk Oblast" },
        { value: "SPE", label: "Saint Petersburg" },
        { value: "STA", label: "Stavropol Krai" },
        { value: "SVE", label: "Sverdlovsk Oblast" },
        { value: "TA", label: "Tatarstan" },
        { value: "TAM", label: "Tambov Oblast" },
        { value: "TOM", label: "Tomsk Oblast" },
        { value: "TUL", label: "Tula Oblast" },
        { value: "TV", label: "Tver Oblast" },
        { value: "TY", label: "Tuva Republic" },
        { value: "TYU", label: "Tyumen Oblast" },
        { value: "UD", label: "Udmurtia" },
        { value: "ULY", label: "Ulyanovsk Oblast" },
        { value: "VGG", label: "Volgograd Oblast" },
        { value: "VLA", label: "Vladimir Oblast" },
        { value: "VLG", label: "Vologda Oblast" },
        { value: "VOR", label: "Voronezh Oblast" },
        { value: "YAN", label: "Yamalo-Nenets" },
        { value: "YAR", label: "Yaroslavl Oblast" },
        { value: "YEV", label: "Jewish Autonomous Oblast" },
        { value: "ZAB", label: "Zabaykalsky Krai" }
    ],
    "PK": [
        { value: "BA", label: "Balochistan" },
        { value: "GB", label: "Gilgit-Baltistan" },
        { value: "IS", label: "Islamabad" },
        { value: "JK", label: "Azad Jammu and Kashmir" },
        { value: "KP", label: "Khyber Pakhtunkhwa" },
        { value: "PB", label: "Punjab" },
        { value: "SD", label: "Sindh" }
    ],
    "MY": [
        { value: "01", label: "Johor" },
        { value: "02", label: "Kedah" },
        { value: "03", label: "Kelantan" },
        { value: "04", label: "Melaka" },
        { value: "05", label: "Negeri Sembilan" },
        { value: "06", label: "Pahang" },
        { value: "07", label: "Pulau Pinang" },
        { value: "08", label: "Perak" },
        { value: "09", label: "Perlis" },
        { value: "10", label: "Selangor" },
        { value: "11", label: "Terengganu" },
        { value: "12", label: "Sabah" },
        { value: "13", label: "Sarawak" },
        { value: "14", label: "Kuala Lumpur" },
        { value: "15", label: "Labuan" },
        { value: "16", label: "Putrajaya" }
    ],
    "TR": [
        { value: "01", label: "Adana" },
        { value: "02", label: "Adıyaman" },
        { value: "03", label: "Afyonkarahisar" },
        { value: "04", label: "Ağrı" },
        { value: "05", label: "Amasya" },
        { value: "06", label: "Ankara" },
        { value: "06-PROV", label: "Ankara Province" }, // Full official name
        { value: "07", label: "Antalya" },
        { value: "08", label: "Artvin" },
        { value: "09", label: "Aydın" },
        { value: "10", label: "Balıkesir" },
        { value: "11", label: "Bilecik" },
        { value: "12", label: "Bingöl" },
        { value: "13", label: "Bitlis" },
        { value: "14", label: "Bolu" },
        { value: "15", label: "Burdur" },
        { value: "16", label: "Bursa" },
        { value: "17", label: "Çanakkale" },
        { value: "18", label: "Çankırı" },
        { value: "19", label: "Çorum" },
        { value: "20", label: "Denizli" },
        { value: "21", label: "Diyarbakır" },
        { value: "22", label: "Edirne" },
        { value: "23", label: "Elazığ" },
        { value: "24", label: "Erzincan" },
        { value: "25", label: "Erzurum" },
        { value: "26", label: "Eskişehir" },
        { value: "27", label: "Gaziantep" },
        { value: "28", label: "Giresun" },
        { value: "29", label: "Gümüşhane" },
        { value: "30", label: "Hakkâri" },
        { value: "31", label: "Hatay" },
        { value: "32", label: "Isparta" },
        { value: "33", label: "Mersin" },
        { value: "34", label: "İstanbul" },
        { value: "35", label: "İzmir" },
        { value: "36", label: "Kars" },
        { value: "37", label: "Kastamonu" },
        { value: "38", label: "Kayseri" },
        { value: "39", label: "Kırklareli" },
        { value: "40", label: "Kırşehir" },
        { value: "41", label: "Kocaeli" },
        { value: "42", label: "Konya" },
        { value: "43", label: "Kütahya" },
        { value: "44", label: "Malatya" },
        { value: "45", label: "Manisa" },
        { value: "46", label: "Kahramanmaraş" },
        { value: "47", label: "Mardin" },
        { value: "48", label: "Muğla" },
        { value: "49", label: "Muş" },
        { value: "50", label: "Nevşehir" },
        { value: "51", label: "Niğde" },
        { value: "52", label: "Ordu" },
        { value: "53", label: "Rize" },
        { value: "54", label: "Sakarya" },
        { value: "55", label: "Samsun" },
        { value: "56", label: "Siirt" },
        { value: "57", label: "Sinop" },
        { value: "58", label: "Sivas" },
        { value: "59", label: "Tekirdağ" },
        { value: "60", label: "Tokat" },
        { value: "61", label: "Trabzon" },
        { value: "62", label: "Tunceli" },
        { value: "63", label: "Şanlıurfa" },
        { value: "64", label: "Uşak" },
        { value: "65", label: "Van" },
        { value: "66", label: "Yozgat" },
        { value: "67", label: "Zonguldak" },
        { value: "68", label: "Aksaray" },
        { value: "69", label: "Bayburt" },
        { value: "70", label: "Karaman" },
        { value: "71", label: "Kırıkkale" },
        { value: "72", label: "Batman" },
        { value: "73", label: "Şırnak" },
        { value: "74", label: "Bartın" },
        { value: "75", label: "Ardahan" },
        { value: "76", label: "Iğdır" },
        { value: "77", label: "Yalova" },
        { value: "78", label: "Karabük" },
        { value: "79", label: "Kilis" },
        { value: "80", label: "Osmaniye" },
        { value: "81", label: "Düzce" }
    ],
    "CN": [
        { value: "AH", label: "Anhui" },
        { value: "BJ", label: "Beijing" },
        { value: "CQ", label: "Chongqing" },
        { value: "FJ", label: "Fujian" },
        { value: "GS", label: "Gansu" },
        { value: "GD", label: "Guangdong" },
        { value: "GX", label: "Guangxi" },
        { value: "GZ", label: "Guizhou" },
        { value: "HI", label: "Hainan" },
        { value: "HE", label: "Hebei" },
        { value: "HL", label: "Heilongjiang" },
        { value: "HA", label: "Henan" },
        { value: "HB", label: "Hubei" },
        { value: "HN", label: "Hunan" },
        { value: "JS", label: "Jiangsu" },
        { value: "JX", label: "Jiangxi" },
        { value: "JL", label: "Jilin" },
        { value: "LN", label: "Liaoning" },
        { value: "NM", label: "Inner Mongolia" },
        { value: "NX", label: "Ningxia" },
        { value: "QH", label: "Qinghai" },
        { value: "SN", label: "Shaanxi" },
        { value: "SD", label: "Shandong" },
        { value: "SH", label: "Shanghai" },
        { value: "SX", label: "Shanxi" },
        { value: "SC", label: "Sichuan" },
        { value: "TJ", label: "Tianjin" },
        { value: "XJ", label: "Xinjiang" },
        { value: "XZ", label: "Tibet" },
        { value: "YN", label: "Yunnan" },
        { value: "ZJ", label: "Zhejiang" },
        { value: "HK", label: "Hong Kong SAR" },
        { value: "MO", label: "Macau SAR" },
        { value: "TW", label: "Taiwan" }
    ],
    "FR": [
        { value: "ARA", label: "Auvergne-Rhône-Alpes" },
        { value: "BFC", label: "Bourgogne-Franche-Comté" },
        { value: "BRE", label: "Brittany" },
        { value: "CVL", label: "Centre-Val de Loire" },
        { value: "COR", label: "Corsica" },
        { value: "GES", label: "Grand Est" },
        { value: "HDF", label: "Hauts-de-France" },
        { value: "IDF", label: "Île-de-France" },
        { value: "NOR", label: "Normandy" },
        { value: "NAQ", label: "Nouvelle-Aquitaine" },
        { value: "OCC", label: "Occitanie" },
        { value: "PDL", label: "Pays de la Loire" },
        { value: "PAC", label: "Provence-Alpes-Côte d'Azur" }
    ],
    "ES": [
        { value: "AN", label: "Andalusia" },
        { value: "AR", label: "Aragon" },
        { value: "AS", label: "Asturias" },
        { value: "IB", label: "Balearic Islands" },
        { value: "PV", label: "Basque Country" },
        { value: "CN", label: "Canary Islands" },
        { value: "CB", label: "Cantabria" },
        { value: "CL", label: "Castile and León" },
        { value: "CM", label: "Castilla-La Mancha" },
        { value: "CT", label: "Catalonia" },
        { value: "EX", label: "Extremadura" },
        { value: "GA", label: "Galicia" },
        { value: "MD", label: "Madrid" },
        { value: "MC", label: "Murcia" },
        { value: "NC", label: "Navarre" },
        { value: "RI", label: "La Rioja" },
        { value: "VC", label: "Valencia" }
    ],
    "IT": [
        { value: "ABR", label: "Abruzzo" },
        { value: "BAS", label: "Basilicata" },
        { value: "CAL", label: "Calabria" },
        { value: "CAM", label: "Campania" },
        { value: "EMR", label: "Emilia-Romagna" },
        { value: "FVG", label: "Friuli-Venezia Giulia" },
        { value: "LAZ", label: "Lazio" },
        { value: "LAZ-ALT", label: "Latium", canonicalName: "Lazio" }, // Latin/alternate English name
        { value: "LIG", label: "Liguria" },
        { value: "LOM", label: "Lombardy" },
        { value: "MAR", label: "Marche" },
        { value: "MOL", label: "Molise" },
        { value: "PMN", label: "Piedmont" },
        { value: "PUG", label: "Apulia" },
        { value: "SAR", label: "Sardinia" },
        { value: "SIC", label: "Sicily" },
        { value: "TOS", label: "Tuscany" },
        { value: "TAA", label: "Trentino-Alto Adige" },
        { value: "UMB", label: "Umbria" },
        { value: "VDA", label: "Aosta Valley" },
        { value: "VEN", label: "Veneto" }
    ],
    "ID": [
        { value: "AC", label: "Aceh" },
        { value: "BA", label: "Bali" },
        { value: "BB", label: "Bangka Belitung Islands" },
        { value: "BT", label: "Banten" },
        { value: "BE", label: "Bengkulu" },
        { value: "GO", label: "Gorontalo" },
        { value: "JK", label: "Jakarta" },
        { value: "JK-FULL", label: "Jakarta Special Capital Region" }, // Full official name
        { value: "JA", label: "Jambi" },
        { value: "JB", label: "West Java" },
        { value: "JT", label: "Central Java" },
        { value: "JI", label: "East Java" },
        { value: "KB", label: "West Kalimantan" },
        { value: "KS", label: "South Kalimantan" },
        { value: "KT", label: "Central Kalimantan" },
        { value: "KI", label: "East Kalimantan" },
        { value: "KU", label: "North Kalimantan" },
        { value: "KR", label: "Riau Islands" },
        { value: "LA", label: "Lampung" },
        { value: "MA", label: "Maluku" },
        { value: "MU", label: "North Maluku" },
        { value: "NB", label: "West Nusa Tenggara" },
        { value: "NT", label: "East Nusa Tenggara" },
        { value: "PA", label: "Papua" },
        { value: "PB", label: "West Papua" },
        { value: "RI", label: "Riau" },
        { value: "SR", label: "West Sulawesi" },
        { value: "SN", label: "South Sulawesi" },
        { value: "ST", label: "Central Sulawesi" },
        { value: "SG", label: "Southeast Sulawesi" },
        { value: "SA", label: "North Sulawesi" },
        { value: "SB", label: "West Sumatra" },
        { value: "SS", label: "South Sumatra" },
        { value: "SU", label: "North Sumatra" },
        { value: "YO", label: "Yogyakarta" }
    ],
    "KR": [
        { value: "11", label: "Seoul" },
        { value: "26", label: "Busan" },
        { value: "27", label: "Daegu" },
        { value: "28", label: "Incheon" },
        { value: "29", label: "Gwangju" },
        { value: "30", label: "Daejeon" },
        { value: "31", label: "Ulsan" },
        { value: "41", label: "Gyeonggi" },
        { value: "42", label: "Gangwon" },
        { value: "43", label: "North Chungcheong" },
        { value: "44", label: "South Chungcheong" },
        { value: "45", label: "North Jeolla" },
        { value: "46", label: "South Jeolla" },
        { value: "47", label: "North Gyeongsang" },
        { value: "48", label: "South Gyeongsang" },
        { value: "49", label: "Jeju" },
        { value: "50", label: "Sejong" }
    ],
    "TH": [
        { value: "10", label: "Bangkok" },
        { value: "11", label: "Samut Prakan" },
        { value: "12", label: "Nonthaburi" },
        { value: "13", label: "Pathum Thani" },
        { value: "14", label: "Phra Nakhon Si Ayutthaya" },
        { value: "15", label: "Ang Thong" },
        { value: "16", label: "Lopburi" },
        { value: "17", label: "Sing Buri" },
        { value: "18", label: "Chai Nat" },
        { value: "19", label: "Saraburi" },
        { value: "20", label: "Chon Buri" },
        { value: "21", label: "Rayong" },
        { value: "22", label: "Chanthaburi" },
        { value: "23", label: "Trat" },
        { value: "24", label: "Chachoengsao" },
        { value: "25", label: "Prachin Buri" },
        { value: "26", label: "Nakhon Nayok" },
        { value: "27", label: "Sa Kaeo" },
        { value: "30", label: "Nakhon Ratchasima" },
        { value: "31", label: "Buri Ram" },
        { value: "32", label: "Surin" },
        { value: "33", label: "Si Sa Ket" },
        { value: "34", label: "Ubon Ratchathani" },
        { value: "35", label: "Yasothon" },
        { value: "36", label: "Chaiyaphum" },
        { value: "37", label: "Amnat Charoen" },
        { value: "38", label: "Bueng Kan" },
        { value: "39", label: "Nong Bua Lam Phu" },
        { value: "40", label: "Khon Kaen" },
        { value: "41", label: "Udon Thani" },
        { value: "42", label: "Loei" },
        { value: "43", label: "Nong Khai" },
        { value: "44", label: "Maha Sarakham" },
        { value: "45", label: "Roi Et" },
        { value: "46", label: "Kalasin" },
        { value: "47", label: "Sakon Nakhon" },
        { value: "48", label: "Nakhon Phanom" },
        { value: "49", label: "Mukdahan" },
        { value: "50", label: "Chiang Mai" },
        { value: "51", label: "Lamphun" },
        { value: "52", label: "Lampang" },
        { value: "53", label: "Uttaradit" },
        { value: "54", label: "Phrae" },
        { value: "55", label: "Nan" },
        { value: "56", label: "Phayao" },
        { value: "57", label: "Chiang Rai" },
        { value: "58", label: "Mae Hong Son" },
        { value: "60", label: "Nakhon Sawan" },
        { value: "61", label: "Uthai Thani" },
        { value: "62", label: "Kamphaeng Phet" },
        { value: "63", label: "Tak" },
        { value: "64", label: "Sukhothai" },
        { value: "65", label: "Phitsanulok" },
        { value: "66", label: "Phichit" },
        { value: "67", label: "Phetchabun" },
        { value: "70", label: "Ratchaburi" },
        { value: "71", label: "Kanchanaburi" },
        { value: "72", label: "Suphan Buri" },
        { value: "73", label: "Nakhon Pathom" },
        { value: "74", label: "Samut Sakhon" },
        { value: "75", label: "Samut Songkhram" },
        { value: "76", label: "Phetchaburi" },
        { value: "77", label: "Prachuap Khiri Khan" },
        { value: "80", label: "Nakhon Si Thammarat" },
        { value: "81", label: "Krabi" },
        { value: "82", label: "Phangnga" },
        { value: "83", label: "Phuket" },
        { value: "84", label: "Surat Thani" },
        { value: "85", label: "Ranong" },
        { value: "86", label: "Chumphon" },
        { value: "90", label: "Songkhla" },
        { value: "91", label: "Satun" },
        { value: "92", label: "Trang" },
        { value: "93", label: "Phatthalung" },
        { value: "94", label: "Pattani" },
        { value: "95", label: "Yala" },
        { value: "96", label: "Narathiwat" }
    ],
    "VN": [
        { value: "01", label: "Hanoi" },
        { value: "02", label: "Ho Chi Minh City" },
        { value: "03", label: "Da Nang" },
        { value: "04", label: "Hai Phong" },
        { value: "05", label: "Can Tho" },
        { value: "06", label: "An Giang" },
        { value: "07", label: "Ba Ria-Vung Tau" },
        { value: "08", label: "Bac Giang" },
        { value: "09", label: "Bac Kan" },
        { value: "10", label: "Bac Lieu" },
        { value: "11", label: "Bac Ninh" },
        { value: "12", label: "Ben Tre" },
        { value: "13", label: "Binh Dinh" },
        { value: "14", label: "Binh Duong" },
        { value: "15", label: "Binh Phuoc" },
        { value: "16", label: "Binh Thuan" },
        { value: "17", label: "Ca Mau" },
        { value: "18", label: "Cao Bang" },
        { value: "19", label: "Dak Lak" },
        { value: "20", label: "Dak Nong" },
        { value: "21", label: "Dien Bien" },
        { value: "22", label: "Dong Nai" },
        { value: "23", label: "Dong Thap" },
        { value: "24", label: "Gia Lai" },
        { value: "25", label: "Ha Giang" },
        { value: "26", label: "Ha Nam" },
        { value: "27", label: "Ha Tinh" },
        { value: "28", label: "Hai Duong" },
        { value: "29", label: "Hau Giang" },
        { value: "30", label: "Hoa Binh" },
        { value: "31", label: "Hung Yen" },
        { value: "32", label: "Khanh Hoa" },
        { value: "33", label: "Kien Giang" },
        { value: "34", label: "Kon Tum" },
        { value: "35", label: "Lai Chau" },
        { value: "36", label: "Lam Dong" },
        { value: "37", label: "Lang Son" },
        { value: "38", label: "Lao Cai" },
        { value: "39", label: "Long An" },
        { value: "40", label: "Nam Dinh" },
        { value: "41", label: "Nghe An" },
        { value: "42", label: "Ninh Binh" },
        { value: "43", label: "Ninh Thuan" },
        { value: "44", label: "Phu Tho" },
        { value: "45", label: "Phu Yen" },
        { value: "46", label: "Quang Binh" },
        { value: "47", label: "Quang Nam" },
        { value: "48", label: "Quang Ngai" },
        { value: "49", label: "Quang Ninh" },
        { value: "50", label: "Quang Tri" },
        { value: "51", label: "Soc Trang" },
        { value: "52", label: "Son La" },
        { value: "53", label: "Tay Ninh" },
        { value: "54", label: "Thai Binh" },
        { value: "55", label: "Thai Nguyen" },
        { value: "56", label: "Thanh Hoa" },
        { value: "57", label: "Thua Thien Hue" },
        { value: "58", label: "Tien Giang" },
        { value: "59", label: "Tra Vinh" },
        { value: "60", label: "Tuyen Quang" },
        { value: "61", label: "Vinh Long" },
        { value: "62", label: "Vinh Phuc" },
        { value: "63", label: "Yen Bai" }
    ],
    "PH": [
        { value: "ABR", label: "Abra" },
        { value: "AGN", label: "Agusan del Norte" },
        { value: "AGS", label: "Agusan del Sur" },
        { value: "AKL", label: "Aklan" },
        { value: "ALB", label: "Albay" },
        { value: "ANT", label: "Antique" },
        { value: "APA", label: "Apayao" },
        { value: "AUR", label: "Aurora" },
        { value: "BAS", label: "Basilan" },
        { value: "BAN", label: "Bataan" },
        { value: "BTN", label: "Batanes" },
        { value: "BTG", label: "Batangas" },
        { value: "BEN", label: "Benguet" },
        { value: "BIL", label: "Biliran" },
        { value: "BOH", label: "Bohol" },
        { value: "BUK", label: "Bukidnon" },
        { value: "BUL", label: "Bulacan" },
        { value: "CAG", label: "Cagayan" },
        { value: "CAN", label: "Camarines Norte" },
        { value: "CAS", label: "Camarines Sur" },
        { value: "CAM", label: "Camiguin" },
        { value: "CAP", label: "Capiz" },
        { value: "CAT", label: "Catanduanes" },
        { value: "CAV", label: "Cavite" },
        { value: "CEB", label: "Cebu" },
        { value: "COM", label: "Compostela Valley" },
        { value: "NCO", label: "Cotabato" },
        { value: "DAV", label: "Davao del Norte" },
        { value: "DAS", label: "Davao del Sur" },
        { value: "DAC", label: "Davao Occidental" },
        { value: "DAO", label: "Davao Oriental" },
        { value: "DIN", label: "Dinagat Islands" },
        { value: "EAS", label: "Eastern Samar" },
        { value: "GUI", label: "Guimaras" },
        { value: "IFU", label: "Ifugao" },
        { value: "ILN", label: "Ilocos Norte" },
        { value: "ILS", label: "Ilocos Sur" },
        { value: "ILI", label: "Iloilo" },
        { value: "ISA", label: "Isabela" },
        { value: "KAL", label: "Kalinga" },
        { value: "LUN", label: "La Union" },
        { value: "LAG", label: "Laguna" },
        { value: "LAN", label: "Lanao del Norte" },
        { value: "LAS", label: "Lanao del Sur" },
        { value: "LEY", label: "Leyte" },
        { value: "MAG", label: "Maguindanao" },
        { value: "MAD", label: "Marinduque" },
        { value: "MAS", label: "Masbate" },
        { value: "MSC", label: "Misamis Occidental" },
        { value: "MSR", label: "Misamis Oriental" },
        { value: "MOU", label: "Mountain Province" },
        { value: "NEC", label: "Negros Occidental" },
        { value: "NER", label: "Negros Oriental" },
        { value: "NSA", label: "Northern Samar" },
        { value: "NUE", label: "Nueva Ecija" },
        { value: "NUV", label: "Nueva Vizcaya" },
        { value: "MDC", label: "Occidental Mindoro" },
        { value: "MDR", label: "Oriental Mindoro" },
        { value: "PLW", label: "Palawan" },
        { value: "PAM", label: "Pampanga" },
        { value: "PAN", label: "Pangasinan" },
        { value: "QUE", label: "Quezon" },
        { value: "QUI", label: "Quirino" },
        { value: "RIZ", label: "Rizal" },
        { value: "ROM", label: "Romblon" },
        { value: "WSA", label: "Samar" },
        { value: "SAR", label: "Sarangani" },
        { value: "SIQ", label: "Siquijor" },
        { value: "SOR", label: "Sorsogon" },
        { value: "SCO", label: "South Cotabato" },
        { value: "SLE", label: "Southern Leyte" },
        { value: "SUK", label: "Sultan Kudarat" },
        { value: "SLU", label: "Sulu" },
        { value: "SUN", label: "Surigao del Norte" },
        { value: "SUR", label: "Surigao del Sur" },
        { value: "TAR", label: "Tarlac" },
        { value: "TAW", label: "Tawi-Tawi" },
        { value: "ZMB", label: "Zambales" },
        { value: "ZAN", label: "Zamboanga del Norte" },
        { value: "ZAS", label: "Zamboanga del Sur" },
        { value: "ZSI", label: "Zamboanga Sibugay" },
        { value: "00", label: "Metro Manila" },
        { value: "NCR", label: "National Capital Region" }, // Official name for Metro Manila
        { value: "NCR-ALT", label: "NCR", canonicalName: "National Capital Region" } // Common abbreviation
    ],
    "EG": [
        { value: "ALX", label: "Alexandria" },
        { value: "ASN", label: "Aswan" },
        { value: "AST", label: "Asyut" },
        { value: "BA", label: "Red Sea" },
        { value: "BH", label: "Beheira" },
        { value: "BNS", label: "Beni Suef" },
        { value: "C", label: "Cairo" },
        { value: "C-GOV", label: "Cairo Governorate" }, // Full official name
        { value: "DK", label: "Dakahlia" },
        { value: "DT", label: "Damietta" },
        { value: "FYM", label: "Faiyum" },
        { value: "GH", label: "Gharbia" },
        { value: "GZ", label: "Giza" },
        { value: "GZ-GOV", label: "Giza Governorate" }, // Full official name
        { value: "IS", label: "Ismailia" },
        { value: "KFS", label: "Kafr el-Sheikh" },
        { value: "LX", label: "Luxor" },
        { value: "MT", label: "Matrouh" },
        { value: "MN", label: "Minya" },
        { value: "MNF", label: "Monufia" },
        { value: "WAD", label: "New Valley" },
        { value: "SIN", label: "North Sinai" },
        { value: "PTS", label: "Port Said" },
        { value: "KB", label: "Qalyubia" },
        { value: "KN", label: "Qena" },
        { value: "SHR", label: "Sharqia" },
        { value: "SHG", label: "Sohag" },
        { value: "JS", label: "South Sinai" },
        { value: "SUZ", label: "Suez" }
    ],
    "PL": [
        { value: "DS", label: "Lower Silesian" },
        { value: "DS-PL", label: "Województwo dolnośląskie", canonicalName: "Lower Silesian" }, // Polish native
        { value: "KP", label: "Kuyavian-Pomeranian" },
        { value: "KP-PL", label: "Województwo kujawsko-pomorskie", canonicalName: "Kuyavian-Pomeranian" }, // Polish native
        { value: "LU", label: "Lublin" },
        { value: "LU-PL", label: "Województwo lubelskie", canonicalName: "Lublin" }, // Polish native
        { value: "LB", label: "Lubusz" },
        { value: "LB-PL", label: "Województwo lubuskie", canonicalName: "Lubusz" }, // Polish native
        { value: "LD", label: "Łódź" },
        { value: "LD-PL", label: "Województwo łódzkie", canonicalName: "Łódź" }, // Polish native
        { value: "MA", label: "Lesser Poland" },
        { value: "MA-PL", label: "Województwo małopolskie", canonicalName: "Lesser Poland" }, // Polish native
        { value: "MZ", label: "Masovian" },
        { value: "MZ-PL", label: "Województwo mazowieckie", canonicalName: "Masovian" }, // Polish native
        { value: "MZ-ALT", label: "Mazowieckie", canonicalName: "Masovian" }, // Short form
        { value: "MZ-EN", label: "Masovian Voivodeship", canonicalName: "Masovian" }, // English formal
        { value: "OP", label: "Opole" },
        { value: "OP-PL", label: "Województwo opolskie", canonicalName: "Opole" }, // Polish native
        { value: "PK", label: "Subcarpathian" },
        { value: "PK-PL", label: "Województwo podkarpackie", canonicalName: "Subcarpathian" }, // Polish native
        { value: "PD", label: "Podlaskie" },
        { value: "PD-PL", label: "Województwo podlaskie", canonicalName: "Podlaskie" }, // Polish native
        { value: "PM", label: "Pomeranian" },
        { value: "PM-PL", label: "Województwo pomorskie", canonicalName: "Pomeranian" }, // Polish native
        { value: "SL", label: "Silesian" },
        { value: "SL-PL", label: "Województwo śląskie", canonicalName: "Silesian" }, // Polish native
        { value: "SL-EN", label: "Silesian Voivodeship", canonicalName: "Silesian" }, // English formal
        { value: "SK", label: "Świętokrzyskie" },
        { value: "SK-PL", label: "Województwo świętokrzyskie", canonicalName: "Świętokrzyskie" }, // Polish native
        { value: "WN", label: "Warmian-Masurian" },
        { value: "WN-PL", label: "Województwo warmińsko-mazurskie", canonicalName: "Warmian-Masurian" }, // Polish native
        { value: "WP", label: "Greater Poland" },
        { value: "WP-PL", label: "Województwo wielkopolskie", canonicalName: "Greater Poland" }, // Polish native
        { value: "ZP", label: "West Pomeranian" },
        { value: "ZP-PL", label: "Województwo zachodniopomorskie", canonicalName: "West Pomeranian" } // Polish native
    ],
    "NL": [
        { value: "DR", label: "Drenthe" },
        { value: "FL", label: "Flevoland" },
        { value: "FR", label: "Friesland" },
        { value: "GE", label: "Gelderland" },
        { value: "GR", label: "Groningen" },
        { value: "LI", label: "Limburg" },
        { value: "NB", label: "North Brabant" },
        { value: "NH", label: "North Holland" },
        { value: "OV", label: "Overijssel" },
        { value: "UT", label: "Utrecht" },
        { value: "ZE", label: "Zeeland" },
        { value: "ZH", label: "South Holland" },
        { value: "ZH-NL", label: "Zuid-Holland", canonicalName: "South Holland" } // Dutch native
    ],
    "BE": [
        // Regions (kept for backwards compatibility)
        { value: "BRU", label: "Brussels" },
        { value: "VLG", label: "Flemish Region" },
        { value: "WAL", label: "Walloon Region" },
        // Flemish Provinces
        { value: "VAN", label: "Antwerp" }, // Antwerpen
        { value: "VBR", label: "Flemish Brabant" }, // Vlaams-Brabant
        { value: "VLI", label: "Limburg" },
        { value: "VOV", label: "East Flanders" }, // Oost-Vlaanderen
        { value: "VWV", label: "West Flanders" }, // West-Vlaanderen
        // Walloon Provinces
        { value: "WHT", label: "Hainaut" }, // Henegouwen
        { value: "WLG", label: "Liège" }, // Luik
        { value: "WLX", label: "Luxembourg" }, // Luxemburg
        { value: "WNA", label: "Namur" }, // Namen
        { value: "WBR", label: "Walloon Brabant" }, // Waals-Brabant
        // Brussels Capital
        { value: "BRU-CAP", label: "Brussels-Capital Region" }
    ],
    "SE": [
        { value: "K", label: "Blekinge" },
        { value: "W", label: "Dalarna" },
        { value: "I", label: "Gotland" },
        { value: "X", label: "Gävleborg" },
        { value: "N", label: "Halland" },
        { value: "Z", label: "Jämtland" },
        { value: "F", label: "Jönköping" },
        { value: "H", label: "Kalmar" },
        { value: "G", label: "Kronoberg" },
        { value: "BD", label: "Norrbotten" },
        { value: "M", label: "Skåne" },
        { value: "AB", label: "Stockholm" },
        { value: "D", label: "Södermanland" },
        { value: "C", label: "Uppsala" },
        { value: "S", label: "Värmland" },
        { value: "AC", label: "Västerbotten" },
        { value: "Y", label: "Västernorrland" },
        { value: "U", label: "Västmanland" },
        { value: "O", label: "Västra Götaland" },
        { value: "T", label: "Örebro" },
        { value: "E", label: "Östergötland" }
    ],
    "NO": [
        { value: "03", label: "Oslo" },
        { value: "11", label: "Rogaland" },
        { value: "15", label: "Møre og Romsdal" },
        { value: "18", label: "Nordland" },
        { value: "30", label: "Viken" },
        { value: "34", label: "Innlandet" },
        { value: "38", label: "Vestfold og Telemark" },
        { value: "42", label: "Agder" },
        { value: "46", label: "Vestland" },
        { value: "50", label: "Trøndelag" },
        { value: "54", label: "Troms og Finnmark" }
    ],
    "IE": [
        { value: "CW", label: "Carlow" },
        { value: "CN", label: "Cavan" },
        { value: "CE", label: "Clare" },
        { value: "CO", label: "Cork" },
        { value: "DL", label: "Donegal" },
        { value: "D", label: "Dublin" },
        { value: "G", label: "Galway" },
        { value: "KY", label: "Kerry" },
        { value: "KE", label: "Kildare" },
        { value: "KK", label: "Kilkenny" },
        { value: "LS", label: "Laois" },
        { value: "LM", label: "Leitrim" },
        { value: "LK", label: "Limerick" },
        { value: "LD", label: "Longford" },
        { value: "LH", label: "Louth" },
        { value: "MO", label: "Mayo" },
        { value: "MH", label: "Meath" },
        { value: "MN", label: "Monaghan" },
        { value: "OY", label: "Offaly" },
        { value: "RN", label: "Roscommon" },
        { value: "SO", label: "Sligo" },
        { value: "TA", label: "Tipperary" },
        { value: "WD", label: "Waterford" },
        { value: "WH", label: "Westmeath" },
        { value: "WX", label: "Wexford" },
        { value: "WW", label: "Wicklow" }
    ],
    "PT": [
        { value: "01", label: "Aveiro" },
        { value: "02", label: "Beja" },
        { value: "03", label: "Braga" },
        { value: "04", label: "Bragança" },
        { value: "05", label: "Castelo Branco" },
        { value: "06", label: "Coimbra" },
        { value: "07", label: "Évora" },
        { value: "08", label: "Faro" },
        { value: "09", label: "Guarda" },
        { value: "10", label: "Leiria" },
        { value: "11", label: "Lisbon" },
        { value: "12", label: "Portalegre" },
        { value: "13", label: "Porto" },
        { value: "14", label: "Santarém" },
        { value: "15", label: "Setúbal" },
        { value: "16", label: "Viana do Castelo" },
        { value: "17", label: "Vila Real" },
        { value: "18", label: "Viseu" },
        { value: "19", label: "Norte" }, // Northern region
        { value: "19-EN", label: "North", canonicalName: "Norte" }, // English translation
        { value: "20", label: "Azores" },
        { value: "30", label: "Madeira" }
    ],
    "GR": [
        { value: "A", label: "Attica" },
        { value: "A-GR", label: "Attiki", canonicalName: "Attica" }, // Greek native (Αττική)
        { value: "I", label: "Central Greece" },
        { value: "II", label: "Central Macedonia" },
        { value: "III", label: "Crete" },
        { value: "IV", label: "Eastern Macedonia and Thrace" },
        { value: "V", label: "Epirus" },
        { value: "VI", label: "Ionian Islands" },
        { value: "VII", label: "North Aegean" },
        { value: "VIII", label: "Peloponnese" },
        { value: "IX", label: "South Aegean" },
        { value: "X", label: "Thessaly" },
        { value: "XI", label: "Western Greece" },
        { value: "XII", label: "Western Macedonia" },
        { value: "XIII", label: "Mount Athos" }
    ],
    "DK": [
        { value: "84", label: "Capital Region" },
        { value: "82", label: "Central Denmark" },
        { value: "82-ALT", label: "Central Jutland", canonicalName: "Central Denmark" }, // Alternate name (Midtjylland)
        { value: "81", label: "North Denmark" },
        { value: "85", label: "Zealand" },
        { value: "83", label: "Southern Denmark" }
    ],
    "FI": [
        { value: "01", label: "Åland" },
        { value: "02", label: "Central Finland" },
        { value: "03", label: "Central Ostrobothnia" },
        { value: "04", label: "Eastern Finland" },
        { value: "05", label: "Finland Proper" },
        { value: "06", label: "Kainuu" },
        { value: "07", label: "Kanta-Häme" },
        { value: "08", label: "Kymenlaakso" },
        { value: "09", label: "Lapland" },
        { value: "10", label: "North Karelia" },
        { value: "11", label: "Northern Ostrobothnia" },
        { value: "11-NS", label: "North Savo", canonicalName: "Northern Savonia" }, // English for Pohjois-Savo
        { value: "11-FI", label: "Pohjois-Savo", canonicalName: "Northern Savonia" }, // Finnish native
        { value: "12", label: "Northern Savonia" },
        { value: "13", label: "Ostrobothnia" },
        { value: "14", label: "Päijänne Tavastia" },
        { value: "15", label: "Pirkanmaa" },
        { value: "16", label: "Satakunta" },
        { value: "17", label: "South Karelia" },
        { value: "18", label: "Southern Ostrobothnia" },
        { value: "19", label: "Southern Savonia" },
        { value: "19-SW", label: "Southwest Finland", canonicalName: "Southwest Finland" }, // English for Varsinais-Suomi (already canonical)
        { value: "19-FI", label: "Varsinais-Suomi", canonicalName: "Southwest Finland" }, // Finnish native
        { value: "20", label: "Uusimaa" }
    ],
    "CZ": [
        { value: "PR", label: "Prague" },
        { value: "PR-CZ", label: "Praha", canonicalName: "Prague" }, // Czech native
        { value: "PL-ALT", label: "Plzensky", canonicalName: "Plzeň" }, // Czech native for Plzeň Region
        { value: "JC-ALT", label: "Jihocesky", canonicalName: "South Bohemian" }, // Czech native for South Bohemian
        { value: "ST", label: "Central Bohemian" },
        { value: "JC", label: "South Bohemian" },
        { value: "PL", label: "Plzeň" },
        { value: "KA", label: "Karlovy Vary" },
        { value: "US", label: "Ústí nad Labem" },
        { value: "LI", label: "Liberec" },
        { value: "KR", label: "Hradec Králové" },
        { value: "PA", label: "Pardubice" },
        { value: "VY", label: "Vysočina" },
        { value: "JM", label: "South Moravian" },
        { value: "OL", label: "Olomouc" },
        { value: "ZL", label: "Zlín" },
        { value: "MS", label: "Moravian-Silesian" }
    ],
    "RO": [
        { value: "AB", label: "Alba" },
        { value: "AR", label: "Arad" },
        { value: "AG", label: "Argeș" },
        { value: "BC", label: "Bacău" },
        { value: "BH", label: "Bihor" },
        { value: "BN", label: "Bistrița-Năsăud" },
        { value: "BT", label: "Botoșani" },
        { value: "BV", label: "Brașov" },
        { value: "BR", label: "Brăila" },
        { value: "B", label: "Bucharest" },
        { value: "B-RO", label: "București", canonicalName: "Bucharest" }, // Romanian native
        { value: "BZ", label: "Buzău" },
        { value: "CS", label: "Caraș-Severin" },
        { value: "CL", label: "Călărași" },
        { value: "CJ", label: "Cluj" },
        { value: "CT", label: "Constanța" },
        { value: "CV", label: "Covasna" },
        { value: "DB", label: "Dâmbovița" },
        { value: "DJ", label: "Dolj" },
        { value: "GL", label: "Galați" },
        { value: "GR", label: "Giurgiu" },
        { value: "GJ", label: "Gorj" },
        { value: "HR", label: "Harghita" },
        { value: "HD", label: "Hunedoara" },
        { value: "IL", label: "Ialomița" },
        { value: "IS", label: "Iași" },
        { value: "IF", label: "Ilfov" },
        { value: "MM", label: "Maramureș" },
        { value: "MH", label: "Mehedinți" },
        { value: "MS", label: "Mureș" },
        { value: "NT", label: "Neamț" },
        { value: "OT", label: "Olt" },
        { value: "PH", label: "Prahova" },
        { value: "SM", label: "Satu Mare" },
        { value: "SJ", label: "Sălaj" },
        { value: "SB", label: "Sibiu" },
        { value: "SV", label: "Suceava" },
        { value: "TR", label: "Teleorman" },
        { value: "TM", label: "Timiș" },
        { value: "TL", label: "Tulcea" },
        { value: "VS", label: "Vaslui" },
        { value: "VL", label: "Vâlcea" },
        { value: "VN", label: "Vrancea" }
    ],
    "HU": [
        { value: "BU", label: "Budapest" },
        { value: "BA", label: "Baranya" },
        { value: "BE", label: "Békés" },
        { value: "BK", label: "Bács-Kiskun" },
        { value: "BZ", label: "Borsod-Abaúj-Zemplén" },
        { value: "CS", label: "Csongrád" },
        { value: "FE", label: "Fejér" },
        { value: "GS", label: "Győr-Moson-Sopron" },
        { value: "HB", label: "Hajdú-Bihar" },
        { value: "HE", label: "Heves" },
        { value: "JN", label: "Jász-Nagykun-Szolnok" },
        { value: "KE", label: "Komárom-Esztergom" },
        { value: "NO", label: "Nógrád" },
        { value: "PE", label: "Pest" },
        { value: "SO", label: "Somogy" },
        { value: "SZ", label: "Szabolcs-Szatmár-Bereg" },
        { value: "TO", label: "Tolna" },
        { value: "VA", label: "Vas" },
        { value: "VE", label: "Veszprém" },
        { value: "ZA", label: "Zala" }
    ],
    "AE": [
        { value: "AZ", label: "Abu Dhabi" },
        { value: "AJ", label: "Ajman" },
        { value: "DU", label: "Dubai" },
        { value: "FU", label: "Fujairah" },
        { value: "RK", label: "Ras Al Khaimah" },
        { value: "SH", label: "Sharjah" },
        { value: "UQ", label: "Umm Al Quwain" }
    ],
    "CO": [
        { value: "AMA", label: "Amazonas" },
        { value: "ANT", label: "Antioquia" },
        { value: "ARA", label: "Arauca" },
        { value: "ATL", label: "Atlántico" },
        { value: "BOL", label: "Bolívar" },
        { value: "BOY", label: "Boyacá" },
        { value: "CAL", label: "Caldas" },
        { value: "CAQ", label: "Caquetá" },
        { value: "CAS", label: "Casanare" },
        { value: "CAU", label: "Cauca" },
        { value: "CES", label: "Cesar" },
        { value: "CHO", label: "Chocó" },
        { value: "COR", label: "Córdoba" },
        { value: "CUN", label: "Cundinamarca" },
        { value: "DC", label: "Bogotá" },
        { value: "GUA", label: "Guainía" },
        { value: "GUV", label: "Guaviare" },
        { value: "HUI", label: "Huila" },
        { value: "LAG", label: "La Guajira" },
        { value: "MAG", label: "Magdalena" },
        { value: "MET", label: "Meta" },
        { value: "NAR", label: "Nariño" },
        { value: "NSA", label: "Norte de Santander" },
        { value: "PUT", label: "Putumayo" },
        { value: "QUI", label: "Quindío" },
        { value: "RIS", label: "Risaralda" },
        { value: "SAP", label: "San Andrés y Providencia" },
        { value: "SAN", label: "Santander" },
        { value: "SUC", label: "Sucre" },
        { value: "TOL", label: "Tolima" },
        { value: "VAC", label: "Valle del Cauca" },
        { value: "VAU", label: "Vaupés" },
        { value: "VID", label: "Vichada" }
    ],
    "CL": [
        { value: "AI", label: "Aysén" },
        { value: "AN", label: "Antofagasta" },
        { value: "AP", label: "Arica y Parinacota" },
        { value: "AT", label: "Atacama" },
        { value: "BI", label: "Biobío" },
        { value: "CO", label: "Coquimbo" },
        { value: "AR", label: "Araucanía" },
        { value: "LI", label: "Libertador General Bernardo O'Higgins" },
        { value: "LL", label: "Los Lagos" },
        { value: "LR", label: "Los Ríos" },
        { value: "MA", label: "Magallanes" },
        { value: "ML", label: "Maule" },
        { value: "NB", label: "Ñuble" },
        { value: "RM", label: "Santiago Metropolitan" },
        { value: "TA", label: "Tarapacá" },
        { value: "VS", label: "Valparaíso" }
    ],
    "NZ": [
        { value: "AUK", label: "Auckland" },
        { value: "BOP", label: "Bay of Plenty" },
        { value: "CAN", label: "Canterbury" },
        { value: "GIS", label: "Gisborne" },
        { value: "HKB", label: "Hawke's Bay" },
        { value: "MWT", label: "Manawatū-Whanganui" },
        { value: "MBH", label: "Marlborough" },
        { value: "NSN", label: "Nelson" },
        { value: "NTL", label: "Northland" },
        { value: "OTA", label: "Otago" },
        { value: "STL", label: "Southland" },
        { value: "TKI", label: "Taranaki" },
        { value: "TAS", label: "Tasman" },
        { value: "WKO", label: "Waikato" },
        { value: "WGN", label: "Wellington" },
        { value: "WTC", label: "West Coast" }
    ],
    "IL": [
        { value: "D", label: "Southern District" },
        { value: "M", label: "Central District" },
        { value: "TA", label: "Tel Aviv District" },
        { value: "HA", label: "Haifa District" },
        { value: "Z", label: "Northern District" },
        { value: "JM", label: "Jerusalem District" }
    ],
    "SA": [
        { value: "01", label: "Riyadh" },
        { value: "02", label: "Makkah" },
        { value: "03", label: "Madinah" },
        { value: "04", label: "Eastern Province" },
        { value: "05", label: "Asir" },
        { value: "06", label: "Tabuk" },
        { value: "07", label: "Qassim" },
        { value: "08", label: "Ha'il" },
        { value: "09", label: "Northern Borders" },
        { value: "10", label: "Jizan" },
        { value: "11", label: "Najran" },
        { value: "12", label: "Al Bahah" },
        { value: "14", label: "Al Jawf" }
    ],
    "SG": [
        { value: "01", label: "Central Region" },
        { value: "01-ALT", label: "Central Singapore", canonicalName: "Central Region" }, // Alternate name
        { value: "02", label: "East Region" },
        { value: "03", label: "North Region" },
        { value: "04", label: "North-East Region" },
        { value: "05", label: "West Region" }
    ],
    "BD": [
        { value: "A", label: "Barishal" },
        { value: "B", label: "Chattogram" },
        { value: "C", label: "Dhaka" },
        { value: "D", label: "Khulna" },
        { value: "E", label: "Rajshahi" },
        { value: "F", label: "Rangpur" },
        { value: "G", label: "Mymensingh" },
        { value: "H", label: "Sylhet" }
    ],
    "KE": [
        { value: "01", label: "Baringo" },
        { value: "02", label: "Bomet" },
        { value: "03", label: "Bungoma" },
        { value: "04", label: "Busia" },
        { value: "05", label: "Elgeyo-Marakwet" },
        { value: "06", label: "Embu" },
        { value: "07", label: "Garissa" },
        { value: "08", label: "Homa Bay" },
        { value: "09", label: "Isiolo" },
        { value: "10", label: "Kajiado" },
        { value: "11", label: "Kakamega" },
        { value: "12", label: "Kericho" },
        { value: "13", label: "Kiambu" },
        { value: "14", label: "Kilifi" },
        { value: "15", label: "Kirinyaga" },
        { value: "16", label: "Kisii" },
        { value: "17", label: "Kisumu" },
        { value: "18", label: "Kitui" },
        { value: "19", label: "Kwale" },
        { value: "20", label: "Laikipia" },
        { value: "21", label: "Lamu" },
        { value: "22", label: "Machakos" },
        { value: "23", label: "Makueni" },
        { value: "24", label: "Mandera" },
        { value: "25", label: "Marsabit" },
        { value: "26", label: "Meru" },
        { value: "27", label: "Migori" },
        { value: "28", label: "Mombasa" },
        { value: "29", label: "Murang'a" },
        { value: "30", label: "Nairobi" },
        { value: "31", label: "Nakuru" },
        { value: "32", label: "Nandi" },
        { value: "33", label: "Narok" },
        { value: "34", label: "Nyamira" },
        { value: "35", label: "Nyandarua" },
        { value: "36", label: "Nyeri" },
        { value: "37", label: "Samburu" },
        { value: "38", label: "Siaya" },
        { value: "39", label: "Taita-Taveta" },
        { value: "40", label: "Tana River" },
        { value: "41", label: "Tharaka-Nithi" },
        { value: "42", label: "Trans Nzoia" },
        { value: "43", label: "Turkana" },
        { value: "44", label: "Uasin Gishu" },
        { value: "45", label: "Vihiga" },
        { value: "46", label: "Wajir" },
        { value: "47", label: "West Pokot" }
    ],
    "UA": [
        { value: "05", label: "Vinnytsia" },
        { value: "07", label: "Volyn" },
        { value: "09", label: "Luhansk" },
        { value: "12", label: "Dnipropetrovsk" },
        { value: "14", label: "Donetsk" },
        { value: "18", label: "Zhytomyr" },
        { value: "21", label: "Zakarpattia" },
        { value: "23", label: "Zaporizhzhia" },
        { value: "26", label: "Ivano-Frankivsk" },
        { value: "30", label: "Kyiv" },
        { value: "30-OBLAST", label: "Kyiv Oblast" }, // Oblast designation
        { value: "30-ALT", label: "Kyiv Region", canonicalName: "Kyiv Oblast" }, // Alternate English name
        { value: "32", label: "Kyiv City" },
        { value: "35", label: "Kirovohrad" },
        { value: "44", label: "Luhansk" },
        { value: "46", label: "Lviv" },
        { value: "48", label: "Mykolaiv" },
        { value: "51", label: "Odesa" },
        { value: "53", label: "Poltava" },
        { value: "56", label: "Rivne" },
        { value: "59", label: "Sumy" },
        { value: "61", label: "Ternopil" },
        { value: "63", label: "Kharkiv" },
        { value: "65", label: "Kherson" },
        { value: "68", label: "Khmelnytskyi" },
        { value: "71", label: "Cherkasy" },
        { value: "74", label: "Chernihiv" },
        { value: "77", label: "Chernivtsi" }
    ],
    "PE": [
        { value: "AMA", label: "Amazonas" },
        { value: "ANC", label: "Áncash" },
        { value: "APU", label: "Apurímac" },
        { value: "ARE", label: "Arequipa" },
        { value: "AYA", label: "Ayacucho" },
        { value: "CAJ", label: "Cajamarca" },
        { value: "CAL", label: "Callao" },
        { value: "CUS", label: "Cusco" },
        { value: "HUV", label: "Huancavelica" },
        { value: "HUC", label: "Huánuco" },
        { value: "ICA", label: "Ica" },
        { value: "JUN", label: "Junín" },
        { value: "LAL", label: "La Libertad" },
        { value: "LAM", label: "Lambayeque" },
        { value: "LIM", label: "Lima" },
        { value: "LOR", label: "Loreto" },
        { value: "MDD", label: "Madre de Dios" },
        { value: "MOQ", label: "Moquegua" },
        { value: "PAS", label: "Pasco" },
        { value: "PIU", label: "Piura" },
        { value: "PUN", label: "Puno" },
        { value: "SAM", label: "San Martín" },
        { value: "TAC", label: "Tacna" },
        { value: "TUM", label: "Tumbes" },
        { value: "UCA", label: "Ucayali" }
    ],
    "MA": [
        { value: "01", label: "Tanger-Tetouan-Al Hoceima" },
        { value: "02", label: "Oriental" },
        { value: "03", label: "Fès-Meknès" },
        { value: "04", label: "Rabat-Salé-Kénitra" },
        { value: "05", label: "Béni Mellal-Khénifra" },
        { value: "06", label: "Casablanca-Settat" },
        { value: "07", label: "Marrakesh-Safi" },
        { value: "08", label: "Drâa-Tafilalet" },
        { value: "09", label: "Souss-Massa" },
        { value: "10", label: "Guelmim-Oued Noun" },
        { value: "11", label: "Laâyoune-Sakia El Hamra" },
        { value: "12", label: "Dakhla-Oued Ed-Dahab" }
    ],
    "KZ": [
        { value: "ALA", label: "Almaty" },
        { value: "ALM", label: "Almaty City" },
        { value: "AKM", label: "Akmola" },
        { value: "AKT", label: "Aktobe" },
        { value: "ATY", label: "Atyrau" },
        { value: "ZAP", label: "West Kazakhstan" },
        { value: "MAN", label: "Mangystau" },
        { value: "YUZ", label: "Turkistan" },
        { value: "PAV", label: "Pavlodar" },
        { value: "KAR", label: "Karaganda" },
        { value: "KOS", label: "Kostanay" },
        { value: "KZY", label: "Kyzylorda" },
        { value: "VOS", label: "East Kazakhstan" },
        { value: "SEV", label: "North Kazakhstan" },
        { value: "AST", label: "Astana" },
        { value: "ZHA", label: "Jambyl" },
        { value: "SHY", label: "Shymkent" }
    ],
    "KW": [
        { value: "AH", label: "Ahmadi" },
        { value: "FA", label: "Farwaniya" },
        { value: "HA", label: "Hawalli" },
        { value: "JA", label: "Jahra" },
        { value: "KU", label: "Kuwait City" },
        { value: "MU", label: "Mubarak Al-Kabeer" }
    ],
    "OM": [
        { value: "DA", label: "Ad Dakhiliyah" },
        { value: "BU", label: "Al Buraimi" },
        { value: "WU", label: "Al Wusta" },
        { value: "ZA", label: "Az Zahirah" },
        { value: "BJ", label: "Janub al Batinah" },
        { value: "SJ", label: "Janub ash Sharqiyah" },
        { value: "MA", label: "Masqat" },
        { value: "MU", label: "Musandam" },
        { value: "BS", label: "Shamal al Batinah" },
        { value: "SS", label: "Shamal ash Sharqiyah" },
        { value: "ZU", label: "Dhofar" }
    ],
    "QA": [
        { value: "DA", label: "Doha" },
        { value: "RA", label: "Al Rayyan" },
        { value: "WA", label: "Al Wakrah" },
        { value: "KH", label: "Al Khor" },
        { value: "MS", label: "Madinat ash Shamal" },
        { value: "SH", label: "Al Shahaniya" },
        { value: "DH", label: "Al Daayen" },
        { value: "US", label: "Umm Salal" }
    ],
    "BH": [
        { value: "13", label: "Capital Governorate" },
        { value: "14", label: "Muharraq Governorate" },
        { value: "15", label: "Northern Governorate" },
        { value: "17", label: "Southern Governorate" }
    ],
    "HR": [
        { value: "01", label: "Zagreb County" },
        { value: "01-HR", label: "Zagrebacka", canonicalName: "Zagreb County" }, // Croatian spelling alternate
        { value: "21-CITY", label: "Zagreb" }, // City reference
        { value: "02", label: "Krapina-Zagorje" },
        { value: "03", label: "Sisak-Moslavina" },
        { value: "04", label: "Karlovac" },
        { value: "05", label: "Varaždin" },
        { value: "06", label: "Koprivnica-Križevci" },
        { value: "07", label: "Bjelovar-Bilogora" },
        { value: "08", label: "Primorje-Gorski Kotar" },
        { value: "09", label: "Lika-Senj" },
        { value: "10", label: "Virovitica-Podravina" },
        { value: "11", label: "Požega-Slavonia" },
        { value: "12", label: "Brod-Posavina" },
        { value: "13", label: "Zadar" },
        { value: "14", label: "Osijek-Baranja" },
        { value: "15", label: "Šibenik-Knin" },
        { value: "16", label: "Vukovar-Syrmia" },
        { value: "17", label: "Split-Dalmatia" },
        { value: "18", label: "Istria" },
        { value: "19", label: "Dubrovnik-Neretva" },
        { value: "20", label: "Međimurje" },
        { value: "21", label: "Zagreb City" }
    ],
    "BG": [
        { value: "01", label: "Blagoevgrad" },
        { value: "02", label: "Burgas" },
        { value: "03", label: "Varna" },
        { value: "04", label: "Veliko Tarnovo" },
        { value: "05", label: "Vidin" },
        { value: "06", label: "Vratsa" },
        { value: "07", label: "Gabrovo" },
        { value: "08", label: "Dobrich" },
        { value: "09", label: "Kardzhali" },
        { value: "10", label: "Kyustendil" },
        { value: "11", label: "Lovech" },
        { value: "12", label: "Montana" },
        { value: "13", label: "Pazardzhik" },
        { value: "14", label: "Pernik" },
        { value: "15", label: "Pleven" },
        { value: "16", label: "Plovdiv" },
        { value: "17", label: "Razgrad" },
        { value: "18", label: "Ruse" },
        { value: "19", label: "Silistra" },
        { value: "20", label: "Sliven" },
        { value: "21", label: "Smolyan" },
        { value: "22", label: "Sofia City" },
        { value: "23", label: "Sofia Province" },
        { value: "24", label: "Stara Zagora" },
        { value: "24-ALT", label: "Zagora", canonicalName: "Stara Zagora" }, // Short form
        { value: "25", label: "Targovishte" },
        { value: "26", label: "Haskovo" },
        { value: "27", label: "Shumen" },
        { value: "28", label: "Yambol" }
    ],
    "SK": [
        { value: "BC", label: "Bratislava" },
        { value: "TA", label: "Trnava" },
        { value: "TC", label: "Trenčín" },
        { value: "NI", label: "Nitra" },
        { value: "ZI", label: "Žilina" },
        { value: "BB", label: "Banská Bystrica" },
        { value: "PV", label: "Prešov" },
        { value: "KI", label: "Košice" }
    ],
    "UY": [
        { value: "AR", label: "Artigas" },
        { value: "CA", label: "Canelones" },
        { value: "CL", label: "Cerro Largo" },
        { value: "CO", label: "Colonia" },
        { value: "DU", label: "Durazno" },
        { value: "FS", label: "Flores" },
        { value: "FD", label: "Florida" },
        { value: "LA", label: "Lavalleja" },
        { value: "MA", label: "Maldonado" },
        { value: "MO", label: "Montevideo" },
        { value: "MO-DEPT", label: "Montevideo Department" }, // Full official name
        { value: "PA", label: "Paysandú" },
        { value: "RN", label: "Río Negro" },
        { value: "RV", label: "Rivera" },
        { value: "RO", label: "Rocha" },
        { value: "SA", label: "Salto" },
        { value: "SJ", label: "San José" },
        { value: "SO", label: "Soriano" },
        { value: "TA", label: "Tacuarembó" },
        { value: "TT", label: "Treinta y Tres" }
    ],
    "CR": [
        { value: "SJ", label: "San José" },
        { value: "A", label: "Alajuela" },
        { value: "C", label: "Cartago" },
        { value: "H", label: "Heredia" },
        { value: "G", label: "Guanacaste" },
        { value: "P", label: "Puntarenas" },
        { value: "L", label: "Limón" }
    ],
    "PA": [
        { value: "1", label: "Bocas del Toro" },
        { value: "2", label: "Coclé" },
        { value: "3", label: "Colón" },
        { value: "4", label: "Chiriquí" },
        { value: "5", label: "Darién" },
        { value: "6", label: "Herrera" },
        { value: "7", label: "Los Santos" },
        { value: "8", label: "Panamá" },
        { value: "9", label: "Veraguas" },
        { value: "10", label: "Panamá Oeste" }
    ],
    "TW": [
        { value: "CHA", label: "Changhua" },
        { value: "CYI", label: "Chiayi" },
        { value: "CYQ", label: "Chiayi City" },
        { value: "HSQ", label: "Hsinchu" },
        { value: "HSZ", label: "Hsinchu City" },
        { value: "HUA", label: "Hualien" },
        { value: "ILA", label: "Yilan" },
        { value: "KEE", label: "Keelung" },
        { value: "KHH", label: "Kaohsiung" },
        { value: "KIN", label: "Kinmen" },
        { value: "LIE", label: "Lienchiang" },
        { value: "MIA", label: "Miaoli" },
        { value: "NAN", label: "Nantou" },
        { value: "NWT", label: "New Taipei" },
        { value: "PEN", label: "Penghu" },
        { value: "PIF", label: "Pingtung" },
        { value: "TAO", label: "Taoyuan" },
        { value: "TNN", label: "Tainan" },
        { value: "TPE", label: "Taipei" },
        { value: "TTT", label: "Taitung" },
        { value: "TXG", label: "Taichung" },
        { value: "YUN", label: "Yunlin" }
    ],
    "LK": [
        { value: "1", label: "Central Province" },
        { value: "2", label: "Eastern Province" },
        { value: "3", label: "North Central Province" },
        { value: "4", label: "Northern Province" },
        { value: "5", label: "North Western Province" },
        { value: "6", label: "Sabaragamuwa Province" },
        { value: "7", label: "Southern Province" },
        { value: "8", label: "Uva Province" },
        { value: "9", label: "Western Province" }
    ],
    "JO": [
        { value: "AJ", label: "Ajloun" },
        { value: "AQ", label: "Aqaba" },
        { value: "AM", label: "Amman" },
        { value: "BA", label: "Balqa" },
        { value: "IR", label: "Irbid" },
        { value: "JA", label: "Jarash" },
        { value: "KA", label: "Karak" },
        { value: "MA", label: "Mafraq" },
        { value: "MD", label: "Madaba" },
        { value: "MN", label: "Ma'an" },
        { value: "TA", label: "Tafilah" },
        { value: "ZA", label: "Zarqa" }
    ],
    "SI": [
        { value: "01", label: "Pomurska" },
        { value: "02", label: "Podravska" },
        { value: "03", label: "Koroška" },
        { value: "04", label: "Savinjska" },
        { value: "05", label: "Zasavska" },
        { value: "06", label: "Posavska" },
        { value: "07", label: "Jugovzhodna Slovenija" },
        { value: "08", label: "Primorsko-notranjska" },
        { value: "09", label: "Osrednjeslovenska" },
        { value: "09-EN", label: "Central Slovenia", canonicalName: "Osrednjeslovenska" }, // English translation (Ljubljana region)
        { value: "10", label: "Gorenjska" },
        { value: "11", label: "Goriška" },
        { value: "12", label: "Obalno-kraška" }
    ],
    "EE": [
        { value: "37", label: "Harju" },
        { value: "37-COUNTY", label: "Harju County", canonicalName: "Harju" }, // English formal name
        { value: "37-ET", label: "Harjumaa", canonicalName: "Harju" }, // Estonian native
        { value: "39", label: "Hiiu" },
        { value: "44", label: "Ida-Viru" },
        { value: "49", label: "Jõgeva" },
        { value: "51", label: "Järva" },
        { value: "57", label: "Lääne" },
        { value: "59", label: "Lääne-Viru" },
        { value: "65", label: "Põlva" },
        { value: "67", label: "Pärnu" },
        { value: "70", label: "Rapla" },
        { value: "74", label: "Saare" },
        { value: "78", label: "Tartu" },
        { value: "82", label: "Valga" },
        { value: "84", label: "Viljandi" },
        { value: "86", label: "Võru" }
    ],
    "LV": [
        { value: "RIX", label: "Riga" },
        { value: "KUR", label: "Kurzeme" },
        { value: "LAT", label: "Latgale" },
        { value: "VID", label: "Vidzeme" },
        { value: "ZEM", label: "Zemgale" }
    ],
    "LT": [
        { value: "AL", label: "Alytus" },
        { value: "KU", label: "Kaunas" },
        { value: "KU-LT", label: "Kauno Apskritis", canonicalName: "Kaunas" }, // Lithuanian native (Kaunas County)
        { value: "KL", label: "Klaipėda" },
        { value: "MR", label: "Marijampolė" },
        { value: "PN", label: "Panevėžys" },
        { value: "SA", label: "Šiauliai" },
        { value: "TA", label: "Tauragė" },
        { value: "TE", label: "Telšiai" },
        { value: "UT", label: "Utena" },
        { value: "VL", label: "Vilnius" }
    ],
    "CY": [
        { value: "01", label: "Famagusta" },
        { value: "02", label: "Kyrenia" },
        { value: "03", label: "Larnaca" },
        { value: "04", label: "Limassol" },
        { value: "05", label: "Nicosia" },
        { value: "06", label: "Paphos" }
    ],
    "LU": [
        { value: "CA", label: "Capellen" },
        { value: "CL", label: "Clervaux" },
        { value: "DI", label: "Diekirch" },
        { value: "EC", label: "Echternach" },
        { value: "ES", label: "Esch-sur-Alzette" },
        { value: "GR", label: "Grevenmacher" },
        { value: "LU", label: "Luxembourg" },
        { value: "ME", label: "Mersch" },
        { value: "RD", label: "Redange" },
        { value: "RM", label: "Remich" },
        { value: "VD", label: "Vianden" },
        { value: "WI", label: "Wiltz" }
    ],
    "MT": [
        { value: "01", label: "Southern Harbour" },
        { value: "02", label: "Northern Harbour" },
        { value: "03", label: "South Eastern" },
        { value: "04", label: "Western" },
        { value: "05", label: "Northern" }
    ],
    "RS": [
        { value: "00", label: "Belgrade" },
        { value: "VO", label: "Vojvodina" }, // Autonomous Province
        { value: "14", label: "Bor" },
        { value: "11", label: "Braničevo" },
        { value: "23", label: "Jablanica" },
        { value: "06", label: "Južna Bačka" },
        { value: "04", label: "Južni Banat" },
        { value: "09", label: "Kolubara" },
        { value: "25", label: "Kosovo" },
        { value: "28", label: "Kosovo-Pomoravlje" },
        { value: "08", label: "Mačva" },
        { value: "17", label: "Moravica" },
        { value: "20", label: "Nišava" },
        { value: "24", label: "Pčinja" },
        { value: "26", label: "Peć" },
        { value: "22", label: "Pirot" },
        { value: "10", label: "Podunavlje" },
        { value: "13", label: "Pomoravlje" },
        { value: "27", label: "Prizren" },
        { value: "19", label: "Rasina" },
        { value: "18", label: "Raška" },
        { value: "01", label: "Severna Bačka" },
        { value: "03", label: "Severni Banat" },
        { value: "02", label: "Srednji Banat" },
        { value: "07", label: "Srem" },
        { value: "12", label: "Šumadija" },
        { value: "21", label: "Toplica" },
        { value: "15", label: "Zaječar" },
        { value: "05", label: "Zapadna Bačka" },
        { value: "16", label: "Zlatibor" }
    ],
    "GH": [
        { value: "AH", label: "Ashanti" },
        { value: "BA", label: "Brong-Ahafo" },
        { value: "BO", label: "Bono" },
        { value: "BE", label: "Bono East" },
        { value: "CP", label: "Central" },
        { value: "EP", label: "Eastern" },
        { value: "AA", label: "Greater Accra" },
        { value: "NE", label: "North East" },
        { value: "NP", label: "Northern" },
        { value: "OT", label: "Oti" },
        { value: "SV", label: "Savannah" },
        { value: "UE", label: "Upper East" },
        { value: "UW", label: "Upper West" },
        { value: "TV", label: "Volta" },
        { value: "WP", label: "Western" },
        { value: "WN", label: "Western North" }
    ],
    "ET": [
        { value: "AA", label: "Addis Ababa" },
        { value: "AF", label: "Afar" },
        { value: "AM", label: "Amhara" },
        { value: "BE", label: "Benishangul-Gumuz" },
        { value: "DD", label: "Dire Dawa" },
        { value: "GA", label: "Gambela" },
        { value: "HA", label: "Harari" },
        { value: "OR", label: "Oromia" },
        { value: "SI", label: "Sidama" },
        { value: "SM", label: "Somali" },
        { value: "SN", label: "Southern Nations" },
        { value: "SW", label: "South West Ethiopia" },
        { value: "TI", label: "Tigray" }
    ],
    "TZ": [
        { value: "01", label: "Arusha" },
        { value: "02", label: "Dar es Salaam" },
        { value: "03", label: "Dodoma" },
        { value: "04", label: "Geita" },
        { value: "05", label: "Iringa" },
        { value: "06", label: "Kagera" },
        { value: "07", label: "Kaskazini Pemba" },
        { value: "08", label: "Kaskazini Unguja" },
        { value: "09", label: "Katavi" },
        { value: "10", label: "Kigoma" },
        { value: "11", label: "Kilimanjaro" },
        { value: "12", label: "Kusini Pemba" },
        { value: "13", label: "Kusini Unguja" },
        { value: "14", label: "Lindi" },
        { value: "15", label: "Manyara" },
        { value: "16", label: "Mara" },
        { value: "17", label: "Mbeya" },
        { value: "18", label: "Mjini Magharibi" },
        { value: "19", label: "Morogoro" },
        { value: "20", label: "Mtwara" },
        { value: "21", label: "Mwanza" },
        { value: "22", label: "Njombe" },
        { value: "23", label: "Pwani" },
        { value: "24", label: "Rukwa" },
        { value: "25", label: "Ruvuma" },
        { value: "26", label: "Shinyanga" },
        { value: "27", label: "Simiyu" },
        { value: "28", label: "Singida" },
        { value: "29", label: "Songwe" },
        { value: "30", label: "Tabora" },
        { value: "31", label: "Tanga" }
    ],
    "UG": [
        { value: "C", label: "Central Region" },
        { value: "E", label: "Eastern Region" },
        { value: "N", label: "Northern Region" },
        { value: "W", label: "Western Region" }
    ],
    "RW": [
        { value: "01", label: "Kigali" },
        { value: "02", label: "Eastern" },
        { value: "03", label: "Northern" },
        { value: "04", label: "Western" },
        { value: "05", label: "Southern" }
    ],
    "DZ": [
        { value: "01", label: "Adrar" },
        { value: "02", label: "Chlef" },
        { value: "03", label: "Laghouat" },
        { value: "04", label: "Oum El Bouaghi" },
        { value: "05", label: "Batna" },
        { value: "06", label: "Béjaïa" },
        { value: "07", label: "Biskra" },
        { value: "08", label: "Béchar" },
        { value: "09", label: "Blida" },
        { value: "10", label: "Bouira" },
        { value: "11", label: "Tamanrasset" },
        { value: "12", label: "Tébessa" },
        { value: "13", label: "Tlemcen" },
        { value: "14", label: "Tiaret" },
        { value: "15", label: "Tizi Ouzou" },
        { value: "16", label: "Algiers" },
        { value: "17", label: "Djelfa" },
        { value: "18", label: "Jijel" },
        { value: "19", label: "Sétif" },
        { value: "20", label: "Saïda" },
        { value: "21", label: "Skikda" },
        { value: "22", label: "Sidi Bel Abbès" },
        { value: "23", label: "Annaba" },
        { value: "24", label: "Guelma" },
        { value: "25", label: "Constantine" },
        { value: "26", label: "Médéa" },
        { value: "27", label: "Mostaganem" },
        { value: "28", label: "M'Sila" },
        { value: "29", label: "Mascara" },
        { value: "30", label: "Ouargla" },
        { value: "31", label: "Oran" },
        { value: "32", label: "El Bayadh" },
        { value: "33", label: "Illizi" },
        { value: "34", label: "Bordj Bou Arréridj" },
        { value: "35", label: "Boumerdès" },
        { value: "36", label: "El Tarf" },
        { value: "37", label: "Tindouf" },
        { value: "38", label: "Tissemsilt" },
        { value: "39", label: "El Oued" },
        { value: "40", label: "Khenchela" },
        { value: "41", label: "Souk Ahras" },
        { value: "42", label: "Tipaza" },
        { value: "43", label: "Mila" },
        { value: "44", label: "Aïn Defla" },
        { value: "45", label: "Naâma" },
        { value: "46", label: "Aïn Témouchent" },
        { value: "47", label: "Ghardaïa" },
        { value: "48", label: "Relizane" },
        { value: "49", label: "Timimoun" },
        { value: "50", label: "Bordj Badji Mokhtar" },
        { value: "51", label: "Ouled Djellal" },
        { value: "52", label: "Béni Abbès" },
        { value: "53", label: "In Salah" },
        { value: "54", label: "In Guezzam" },
        { value: "55", label: "Touggourt" },
        { value: "56", label: "Djanet" },
        { value: "57", label: "El M'Ghair" },
        { value: "58", label: "El Meniaa" }
    ],
    "TN": [
        { value: "11", label: "Tunis" },
        { value: "12", label: "Ariana" },
        { value: "13", label: "Ben Arous" },
        { value: "14", label: "Manouba" },
        { value: "21", label: "Nabeul" },
        { value: "22", label: "Zaghouan" },
        { value: "23", label: "Bizerte" },
        { value: "31", label: "Béja" },
        { value: "32", label: "Jendouba" },
        { value: "33", label: "Kef" },
        { value: "34", label: "Siliana" },
        { value: "41", label: "Kairouan" },
        { value: "42", label: "Kasserine" },
        { value: "43", label: "Sidi Bouzid" },
        { value: "51", label: "Sousse" },
        { value: "52", label: "Monastir" },
        { value: "53", label: "Mahdia" },
        { value: "61", label: "Sfax" },
        { value: "71", label: "Gafsa" },
        { value: "72", label: "Tozeur" },
        { value: "73", label: "Kebili" },
        { value: "81", label: "Gabès" },
        { value: "82", label: "Medenine" },
        { value: "83", label: "Tataouine" }
    ],
    "EC": [
        { value: "A", label: "Azuay" },
        { value: "B", label: "Bolívar" },
        { value: "F", label: "Cañar" },
        { value: "C", label: "Carchi" },
        { value: "H", label: "Chimborazo" },
        { value: "X", label: "Cotopaxi" },
        { value: "O", label: "El Oro" },
        { value: "E", label: "Esmeraldas" },
        { value: "W", label: "Galápagos" },
        { value: "G", label: "Guayas" },
        { value: "I", label: "Imbabura" },
        { value: "L", label: "Loja" },
        { value: "R", label: "Los Ríos" },
        { value: "M", label: "Manabí" },
        { value: "S", label: "Morona Santiago" },
        { value: "N", label: "Napo" },
        { value: "D", label: "Orellana" },
        { value: "Y", label: "Pastaza" },
        { value: "P", label: "Pichincha" },
        { value: "SE", label: "Santa Elena" },
        { value: "SD", label: "Santo Domingo de los Tsáchilas" },
        { value: "U", label: "Sucumbíos" },
        { value: "T", label: "Tungurahua" },
        { value: "Z", label: "Zamora Chinchipe" }
    ],
    "GT": [
        { value: "AV", label: "Alta Verapaz" },
        { value: "BV", label: "Baja Verapaz" },
        { value: "CM", label: "Chimaltenango" },
        { value: "CQ", label: "Chiquimula" },
        { value: "PR", label: "El Progreso" },
        { value: "ES", label: "Escuintla" },
        { value: "GU", label: "Guatemala" },
        { value: "HU", label: "Huehuetenango" },
        { value: "IZ", label: "Izabal" },
        { value: "JA", label: "Jalapa" },
        { value: "JU", label: "Jutiapa" },
        { value: "PE", label: "Petén" },
        { value: "QZ", label: "Quetzaltenango" },
        { value: "QC", label: "Quiché" },
        { value: "RE", label: "Retalhuleu" },
        { value: "SA", label: "Sacatepéquez" },
        { value: "SM", label: "San Marcos" },
        { value: "SR", label: "Santa Rosa" },
        { value: "SO", label: "Sololá" },
        { value: "SU", label: "Suchitepéquez" },
        { value: "TO", label: "Totonicapán" },
        { value: "ZA", label: "Zacapa" }
    ],
    "DO": [
        { value: "01", label: "Distrito Nacional" },
        { value: "02", label: "Azua" },
        { value: "03", label: "Baoruco" },
        { value: "04", label: "Barahona" },
        { value: "05", label: "Dajabón" },
        { value: "06", label: "Duarte" },
        { value: "07", label: "Elías Piña" },
        { value: "08", label: "El Seibo" },
        { value: "09", label: "Espaillat" },
        { value: "10", label: "Independencia" },
        { value: "11", label: "La Altagracia" },
        { value: "12", label: "La Romana" },
        { value: "13", label: "La Vega" },
        { value: "14", label: "María Trinidad Sánchez" },
        { value: "15", label: "Monte Cristi" },
        { value: "16", label: "Monte Plata" },
        { value: "17", label: "Pedernales" },
        { value: "18", label: "Peravia" },
        { value: "19", label: "Puerto Plata" },
        { value: "20", label: "Hermanas Mirabal" },
        { value: "21", label: "Samaná" },
        { value: "22", label: "San Cristóbal" },
        { value: "23", label: "San José de Ocoa" },
        { value: "24", label: "San Juan" },
        { value: "25", label: "San Pedro de Macorís" },
        { value: "26", label: "Sánchez Ramírez" },
        { value: "27", label: "Santiago" },
        { value: "28", label: "Santiago Rodríguez" },
        { value: "29", label: "Santo Domingo" },
        { value: "30", label: "Valverde" },
        { value: "31", label: "Monseñor Nouel" },
        { value: "32", label: "Hato Mayor" }
    ],
    "VE": [
        { value: "Z", label: "Amazonas" },
        { value: "B", label: "Anzoátegui" },
        { value: "C", label: "Apure" },
        { value: "D", label: "Aragua" },
        { value: "E", label: "Barinas" },
        { value: "F", label: "Bolívar" },
        { value: "G", label: "Carabobo" },
        { value: "H", label: "Cojedes" },
        { value: "Y", label: "Delta Amacuro" },
        { value: "W", label: "Dependencias Federales" },
        { value: "A", label: "Distrito Capital" },
        { value: "I", label: "Falcón" },
        { value: "J", label: "Guárico" },
        { value: "K", label: "Lara" },
        { value: "L", label: "Mérida" },
        { value: "M", label: "Miranda" },
        { value: "N", label: "Monagas" },
        { value: "O", label: "Nueva Esparta" },
        { value: "P", label: "Portuguesa" },
        { value: "R", label: "Sucre" },
        { value: "S", label: "Táchira" },
        { value: "T", label: "Trujillo" },
        { value: "U", label: "Yaracuy" },
        { value: "V", label: "Zulia" }
    ],
    "BO": [
        { value: "B", label: "El Beni" },
        { value: "H", label: "Chuquisaca" },
        { value: "C", label: "Cochabamba" },
        { value: "L", label: "La Paz" },
        { value: "O", label: "Oruro" },
        { value: "N", label: "Pando" },
        { value: "P", label: "Potosí" },
        { value: "S", label: "Santa Cruz" },
        { value: "T", label: "Tarija" }
    ],
    "PY": [
        { value: "16", label: "Alto Paraguay" },
        { value: "10", label: "Alto Paraná" },
        { value: "13", label: "Amambay" },
        { value: "ASU", label: "Asunción" },
        { value: "19", label: "Boquerón" },
        { value: "5", label: "Caaguazú" },
        { value: "6", label: "Caazapá" },
        { value: "14", label: "Canindeyú" },
        { value: "11", label: "Central" },
        { value: "1", label: "Concepción" },
        { value: "3", label: "Cordillera" },
        { value: "4", label: "Guairá" },
        { value: "7", label: "Itapúa" },
        { value: "8", label: "Misiones" },
        { value: "9", label: "Paraguarí" },
        { value: "15", label: "Presidente Hayes" },
        { value: "2", label: "San Pedro" },
        { value: "12", label: "Ñeembucú" }
    ],
    "HN": [
        { value: "AT", label: "Atlántida" },
        { value: "CH", label: "Choluteca" },
        { value: "CL", label: "Colón" },
        { value: "CM", label: "Comayagua" },
        { value: "CP", label: "Copán" },
        { value: "CR", label: "Cortés" },
        { value: "EP", label: "El Paraíso" },
        { value: "FM", label: "Francisco Morazán" },
        { value: "GD", label: "Gracias a Dios" },
        { value: "IN", label: "Intibucá" },
        { value: "IB", label: "Islas de la Bahía" },
        { value: "LP", label: "La Paz" },
        { value: "LE", label: "Lempira" },
        { value: "OC", label: "Ocotepeque" },
        { value: "OL", label: "Olancho" },
        { value: "SB", label: "Santa Bárbara" },
        { value: "VA", label: "Valle" },
        { value: "YO", label: "Yoro" }
    ],
    "NI": [
        { value: "BO", label: "Boaco" },
        { value: "CA", label: "Carazo" },
        { value: "CI", label: "Chinandega" },
        { value: "CO", label: "Chontales" },
        { value: "AN", label: "Costa Caribe Norte" },
        { value: "AS", label: "Costa Caribe Sur" },
        { value: "ES", label: "Estelí" },
        { value: "GR", label: "Granada" },
        { value: "JI", label: "Jinotega" },
        { value: "LE", label: "León" },
        { value: "MD", label: "Madriz" },
        { value: "MN", label: "Managua" },
        { value: "MS", label: "Masaya" },
        { value: "MT", label: "Matagalpa" },
        { value: "NS", label: "Nueva Segovia" },
        { value: "RI", label: "Rivas" },
        { value: "SJ", label: "Río San Juan" }
    ],
    "SV": [
        { value: "AH", label: "Ahuachapán" },
        { value: "CA", label: "Cabañas" },
        { value: "CH", label: "Chalatenango" },
        { value: "CU", label: "Cuscatlán" },
        { value: "LI", label: "La Libertad" },
        { value: "PA", label: "La Paz" },
        { value: "UN", label: "La Unión" },
        { value: "MO", label: "Morazán" },
        { value: "SM", label: "San Miguel" },
        { value: "SS", label: "San Salvador" },
        { value: "SV", label: "San Vicente" },
        { value: "SA", label: "Santa Ana" },
        { value: "SO", label: "Sonsonate" },
        { value: "US", label: "Usulután" }
    ],
    "MM": [
        { value: "01", label: "Sagaing" },
        { value: "02", label: "Bago" },
        { value: "03", label: "Magway" },
        { value: "04", label: "Mandalay" },
        { value: "05", label: "Tanintharyi" },
        { value: "06", label: "Yangon" },
        { value: "07", label: "Ayeyarwady" },
        { value: "11", label: "Kachin" },
        { value: "12", label: "Kayah" },
        { value: "13", label: "Kayin" },
        { value: "14", label: "Chin" },
        { value: "15", label: "Mon" },
        { value: "16", label: "Rakhine" },
        { value: "17", label: "Shan" },
        { value: "18", label: "Naypyidaw" }
    ],
    "KH": [
        { value: "1", label: "Banteay Meanchey" },
        { value: "2", label: "Battambang" },
        { value: "3", label: "Kampong Cham" },
        { value: "4", label: "Kampong Chhnang" },
        { value: "5", label: "Kampong Speu" },
        { value: "6", label: "Kampong Thom" },
        { value: "7", label: "Kampot" },
        { value: "8", label: "Kandal" },
        { value: "9", label: "Kep" },
        { value: "10", label: "Koh Kong" },
        { value: "11", label: "Kratié" },
        { value: "12", label: "Mondulkiri" },
        { value: "13", label: "Oddar Meanchey" },
        { value: "14", label: "Pailin" },
        { value: "15", label: "Phnom Penh" },
        { value: "16", label: "Preah Sihanouk" },
        { value: "17", label: "Preah Vihear" },
        { value: "18", label: "Pursat" },
        { value: "19", label: "Prey Veng" },
        { value: "20", label: "Ratanakiri" },
        { value: "21", label: "Siem Reap" },
        { value: "22", label: "Stung Treng" },
        { value: "23", label: "Svay Rieng" },
        { value: "24", label: "Takéo" },
        { value: "25", label: "Tboung Khmum" }
    ],
    "LA": [
        { value: "AT", label: "Attapeu" },
        { value: "BK", label: "Bokeo" },
        { value: "BL", label: "Bolikhamsai" },
        { value: "CH", label: "Champasak" },
        { value: "HO", label: "Houaphanh" },
        { value: "KH", label: "Khammouane" },
        { value: "LM", label: "Luang Namtha" },
        { value: "LP", label: "Luang Prabang" },
        { value: "OU", label: "Oudomxay" },
        { value: "PH", label: "Phongsaly" },
        { value: "SL", label: "Salavan" },
        { value: "SV", label: "Savannakhet" },
        { value: "VI", label: "Vientiane" },
        { value: "VT", label: "Vientiane Prefecture" },
        { value: "XA", label: "Xayaboury" },
        { value: "XE", label: "Xekong" },
        { value: "XI", label: "Xiangkhouang" },
        { value: "XS", label: "Xaisomboun" }
    ],
    "NP": [
        { value: "P1", label: "Province 1" },
        { value: "P2", label: "Madhesh" },
        { value: "P3", label: "Bagmati" },
        { value: "P4", label: "Gandaki" },
        { value: "P5", label: "Lumbini" },
        { value: "P6", label: "Karnali" },
        { value: "P7", label: "Sudurpashchim" }
    ],
    "MN": [
        { value: "073", label: "Arkhangai" },
        { value: "069", label: "Bayankhongor" },
        { value: "071", label: "Bayan-Ölgii" },
        { value: "067", label: "Bulgan" },
        { value: "037", label: "Darkhan-Uul" },
        { value: "061", label: "Dornod" },
        { value: "063", label: "Dornogovi" },
        { value: "059", label: "Dundgovi" },
        { value: "057", label: "Govi-Altai" },
        { value: "065", label: "Govisümber" },
        { value: "039", label: "Khentii" },
        { value: "041", label: "Khovd" },
        { value: "043", label: "Khövsgöl" },
        { value: "035", label: "Orkhon" },
        { value: "049", label: "Ömnögovi" },
        { value: "055", label: "Övörkhangai" },
        { value: "047", label: "Selenge" },
        { value: "051", label: "Sükhbaatar" },
        { value: "045", label: "Töv" },
        { value: "1", label: "Ulaanbaatar" },
        { value: "046", label: "Uvs" }
    ],
    "LB": [
        { value: "AK", label: "Akkar" },
        { value: "BH", label: "Baalbek-Hermel" },
        { value: "BA", label: "Beirut" },
        { value: "BA-GOV", label: "Beirut Governorate" }, // Full official name
        { value: "BI", label: "Beqaa" },
        { value: "AS", label: "Mount Lebanon" },
        { value: "NA", label: "Nabatieh" },
        { value: "JA", label: "North" },
        { value: "JL", label: "South" }
    ],
    "IQ": [
        { value: "AN", label: "Al Anbar" },
        { value: "BA", label: "Basra" },
        { value: "MU", label: "Al Muthanna" },
        { value: "QA", label: "Al Qādisiyyah" },
        { value: "NA", label: "Najaf" },
        { value: "AR", label: "Erbil" },
        { value: "SU", label: "Sulaymaniyah" },
        { value: "TS", label: "Salah ad Din" },
        { value: "DI", label: "Diyala" },
        { value: "BG", label: "Baghdad" },
        { value: "NI", label: "Nineveh" },
        { value: "DA", label: "Duhok" },
        { value: "KA", label: "Karbala" },
        { value: "BB", label: "Babylon" },
        { value: "WA", label: "Wasit" },
        { value: "MA", label: "Maysan" },
        { value: "DQ", label: "Dhi Qar" },
        { value: "SD", label: "Saladin" },
        { value: "KI", label: "Kirkuk" }
    ],
    "IR": [
        { value: "32", label: "Alborz" },
        { value: "03", label: "Ardabil" },
        { value: "02", label: "West Azerbaijan" },
        { value: "01", label: "East Azerbaijan" },
        { value: "06", label: "Bushehr" },
        { value: "08", label: "Chaharmahal and Bakhtiari" },
        { value: "04", label: "Fars" },
        { value: "19", label: "Gilan" },
        { value: "27", label: "Golestan" },
        { value: "24", label: "Hamadan" },
        { value: "23", label: "Hormozgan" },
        { value: "05", label: "Ilam" },
        { value: "15", label: "Isfahan" },
        { value: "29", label: "Kerman" },
        { value: "17", label: "Kermanshah" },
        { value: "09", label: "North Khorasan" },
        { value: "10", label: "Razavi Khorasan" },
        { value: "11", label: "South Khorasan" },
        { value: "12", label: "Khuzestan" },
        { value: "13", label: "Kohgiluyeh and Boyer-Ahmad" },
        { value: "14", label: "Kurdistan" },
        { value: "16", label: "Lorestan" },
        { value: "20", label: "Markazi" },
        { value: "21", label: "Mazandaran" },
        { value: "22", label: "Qazvin" },
        { value: "28", label: "Qom" },
        { value: "25", label: "Semnan" },
        { value: "26", label: "Sistan and Baluchestan" },
        { value: "07", label: "Tehran" },
        { value: "30", label: "Yazd" },
        { value: "31", label: "Zanjan" }
    ],
    "GE": [
        { value: "AB", label: "Abkhazia" },
        { value: "AJ", label: "Adjara" },
        { value: "GU", label: "Guria" },
        { value: "IM", label: "Imereti" },
        { value: "KA", label: "Kakheti" },
        { value: "KK", label: "Kvemo Kartli" },
        { value: "MM", label: "Mtskheta-Mtianeti" },
        { value: "RL", label: "Racha-Lechkhumi" },
        { value: "SZ", label: "Samegrelo-Zemo Svaneti" },
        { value: "SJ", label: "Samtskhe-Javakheti" },
        { value: "SK", label: "Shida Kartli" },
        { value: "TB", label: "Tbilisi" }
    ],
    "AM": [
        { value: "AG", label: "Aragatsotn" },
        { value: "AR", label: "Ararat" },
        { value: "AV", label: "Armavir" },
        { value: "ER", label: "Yerevan" },
        { value: "GR", label: "Gegharkunik" },
        { value: "KT", label: "Kotayk" },
        { value: "LO", label: "Lori" },
        { value: "SH", label: "Shirak" },
        { value: "SU", label: "Syunik" },
        { value: "TV", label: "Tavush" },
        { value: "VD", label: "Vayots Dzor" }
    ],
    "AZ": [
        { value: "ABS", label: "Absheron" },
        { value: "AGA", label: "Agstafa" },
        { value: "BA", label: "Baku" },
        { value: "GAL", label: "Ganja-Qazakh" },
        { value: "GOR", label: "Guba-Khachmaz" },
        { value: "KAL", label: "Kalbajar-Lachin" },
        { value: "LAN", label: "Lankaran" },
        { value: "MIL", label: "Mountainous Shirvan" },
        { value: "NX", label: "Nakhchivan" },
        { value: "SAL", label: "Salyan" },
        { value: "SMI", label: "Shaki-Zagatala" },
        { value: "SMX", label: "Shirvan-Salyan" },
        { value: "SR", label: "Shirvan" },
        { value: "YEV", label: "Yevlakh-Agdash" }
    ],
    "BY": [
        { value: "BR", label: "Brest" },
        { value: "HO", label: "Gomel" },
        { value: "HR", label: "Grodno" },
        { value: "HM", label: "Minsk" },
        { value: "MI", label: "Minsk City" },
        { value: "MA", label: "Mogilev" },
        { value: "VI", label: "Vitebsk" }
    ],
    "MD": [
        { value: "C", label: "Chișinău" },
        { value: "BA", label: "Bălți" },
        { value: "CA", label: "Cahul" },
        { value: "GA", label: "Gagauzia" },
        { value: "SN", label: "Transnistria" }
    ],
    "BA": [
        { value: "BRC", label: "Brčko District" },
        { value: "BIH", label: "Federation of Bosnia and Herzegovina" },
        { value: "SRP", label: "Republika Srpska" }
    ],
    "ME": [
        { value: "01", label: "Andrijevica" },
        { value: "02", label: "Bar" },
        { value: "03", label: "Berane" },
        { value: "04", label: "Bijelo Polje" },
        { value: "05", label: "Budva" },
        { value: "06", label: "Cetinje" },
        { value: "07", label: "Danilovgrad" },
        { value: "08", label: "Herceg Novi" },
        { value: "09", label: "Kolašin" },
        { value: "10", label: "Kotor" },
        { value: "11", label: "Mojkovac" },
        { value: "12", label: "Nikšić" },
        { value: "13", label: "Plav" },
        { value: "14", label: "Plužine" },
        { value: "15", label: "Pljevlja" },
        { value: "16", label: "Podgorica" },
        { value: "17", label: "Rožaje" },
        { value: "18", label: "Šavnik" },
        { value: "19", label: "Tivat" },
        { value: "20", label: "Ulcinj" },
        { value: "21", label: "Žabljak" },
        { value: "22", label: "Gusinje" },
        { value: "23", label: "Petnjica" },
        { value: "24", label: "Tuzi" }
    ],
    "AL": [
        { value: "01", label: "Berat" },
        { value: "09", label: "Dibër" },
        { value: "02", label: "Durrës" },
        { value: "03", label: "Elbasan" },
        { value: "04", label: "Fier" },
        { value: "05", label: "Gjirokastër" },
        { value: "06", label: "Korçë" },
        { value: "07", label: "Kukës" },
        { value: "08", label: "Lezhë" },
        { value: "10", label: "Shkodër" },
        { value: "11", label: "Tirana" },
        { value: "12", label: "Vlorë" }
    ],
    "MK": [
        { value: "101", label: "Skopje" },
        { value: "102", label: "Pelagonia" },
        { value: "103", label: "Polog" },
        { value: "104", label: "Northeastern" },
        { value: "105", label: "Southwestern" },
        { value: "106", label: "Vardar" },
        { value: "107", label: "Eastern" },
        { value: "108", label: "Southeastern" }
    ],
    "UZ": [
        { value: "AN", label: "Andijan" },
        { value: "BU", label: "Bukhara" },
        { value: "FA", label: "Fergana" },
        { value: "JI", label: "Jizzakh" },
        { value: "KH", label: "Khorezm" },
        { value: "NG", label: "Namangan" },
        { value: "NW", label: "Navoiy" },
        { value: "QA", label: "Qashqadaryo" },
        { value: "QR", label: "Karakalpakstan" },
        { value: "SA", label: "Samarkand" },
        { value: "SI", label: "Sirdaryo" },
        { value: "SU", label: "Surxondaryo" },
        { value: "TK", label: "Tashkent" },
        { value: "TO", label: "Tashkent City" }
    ],
    "TM": [
        { value: "A", label: "Ahal" },
        { value: "B", label: "Balkan" },
        { value: "D", label: "Daşoguz" },
        { value: "L", label: "Lebap" },
        { value: "M", label: "Mary" },
        { value: "S", label: "Ashgabat" }
    ],
    "TJ": [
        { value: "DU", label: "Dushanbe" },
        { value: "GB", label: "Gorno-Badakhshan" },
        { value: "KT", label: "Khatlon" },
        { value: "RA", label: "Sughd" },
        { value: "RR", label: "Districts of Republican Subordination" }
    ],
    "KG": [
        { value: "B", label: "Batken" },
        { value: "C", label: "Chuy" },
        { value: "GB", label: "Bishkek" },
        { value: "GO", label: "Osh City" },
        { value: "J", label: "Jalal-Abad" },
        { value: "N", label: "Naryn" },
        { value: "O", label: "Osh" },
        { value: "T", label: "Talas" },
        { value: "Y", label: "Ysyk-Köl" }
    ],
    "YE": [
        { value: "AB", label: "Abyan" },
        { value: "AD", label: "Aden" },
        { value: "AM", label: "Amran" },
        { value: "BA", label: "Al Bayda" },
        { value: "DA", label: "Ad Dali" },
        { value: "DH", label: "Dhamar" },
        { value: "HD", label: "Hadramaut" },
        { value: "HJ", label: "Hajjah" },
        { value: "HU", label: "Al Hudaydah" },
        { value: "IB", label: "Ibb" },
        { value: "JA", label: "Al Jawf" },
        { value: "LA", label: "Lahij" },
        { value: "MA", label: "Ma'rib" },
        { value: "MR", label: "Al Mahrah" },
        { value: "MW", label: "Al Mahwit" },
        { value: "RA", label: "Raymah" },
        { value: "SA", label: "Sana'a" },
        { value: "SD", label: "Sa'dah" },
        { value: "SH", label: "Shabwah" },
        { value: "SN", label: "Sana'a City" },
        { value: "SU", label: "Socotra" },
        { value: "TA", label: "Ta'izz" }
    ],
    "SY": [
        { value: "HL", label: "Aleppo" },
        { value: "HA", label: "Al-Hasakah" },
        { value: "DI", label: "Damascus" },
        { value: "DR", label: "Daraa" },
        { value: "DY", label: "Deir ez-Zor" },
        { value: "DM", label: "Damascus Countryside" },
        { value: "HM", label: "Hama" },
        { value: "HI", label: "Homs" },
        { value: "ID", label: "Idlib" },
        { value: "LA", label: "Latakia" },
        { value: "QU", label: "Quneitra" },
        { value: "RA", label: "Ar-Raqqah" },
        { value: "SU", label: "As-Suwayda" },
        { value: "TA", label: "Tartus" }
    ],
    "PS": [
        { value: "BTH", label: "Bethlehem" },
        { value: "DEB", label: "Deir al-Balah" },
        { value: "GZA", label: "Gaza" },
        { value: "HBN", label: "Hebron" },
        { value: "JEN", label: "Jenin" },
        { value: "JRH", label: "Jericho" },
        { value: "JEM", label: "Jerusalem" },
        { value: "KYS", label: "Khan Yunis" },
        { value: "NBS", label: "Nablus" },
        { value: "NGZ", label: "North Gaza" },
        { value: "QQA", label: "Qalqilya" },
        { value: "RBH", label: "Ramallah" },
        { value: "RFH", label: "Rafah" },
        { value: "SLT", label: "Salfit" },
        { value: "TBS", label: "Tubas" },
        { value: "TKM", label: "Tulkarm" }
    ],
    "CD": [
        { value: "BC", label: "Bas-Congo" },
        { value: "BN", label: "Bandundu" },
        { value: "EQ", label: "Équateur" },
        { value: "KA", label: "Katanga" },
        { value: "KE", label: "Kasai-Oriental" },
        { value: "KN", label: "Kinshasa" },
        { value: "KW", label: "Kasai-Occidental" },
        { value: "MA", label: "Maniema" },
        { value: "NK", label: "Nord-Kivu" },
        { value: "OR", label: "Orientale" },
        { value: "SK", label: "Sud-Kivu" }
    ],
    "CI": [
        { value: "AB", label: "Abidjan" },
        { value: "BS", label: "Bas-Sassandra" },
        { value: "CM", label: "Comoé" },
        { value: "DN", label: "Denguélé" },
        { value: "GD", label: "Gôh-Djiboua" },
        { value: "LC", label: "Lacs" },
        { value: "LG", label: "Lagunes" },
        { value: "MG", label: "Montagnes" },
        { value: "SM", label: "Sassandra-Marahoué" },
        { value: "SV", label: "Savanes" },
        { value: "VB", label: "Vallée du Bandama" },
        { value: "WR", label: "Woroba" },
        { value: "YM", label: "Yamoussoukro" },
        { value: "ZZ", label: "Zanzan" }
    ],
    "SN": [
        { value: "DB", label: "Diourbel" },
        { value: "DK", label: "Dakar" },
        { value: "FK", label: "Fatick" },
        { value: "KA", label: "Kaffrine" },
        { value: "KD", label: "Kolda" },
        { value: "KE", label: "Kédougou" },
        { value: "KL", label: "Kaolack" },
        { value: "LG", label: "Louga" },
        { value: "MT", label: "Matam" },
        { value: "SE", label: "Sédhiou" },
        { value: "SL", label: "Saint-Louis" },
        { value: "TC", label: "Tambacounda" },
        { value: "TH", label: "Thiès" },
        { value: "ZG", label: "Ziguinchor" }
    ],
    "AO": [
        { value: "BGO", label: "Bengo" },
        { value: "BGU", label: "Benguela" },
        { value: "BIE", label: "Bié" },
        { value: "CAB", label: "Cabinda" },
        { value: "CCU", label: "Cuando Cubango" },
        { value: "CNN", label: "Cunene" },
        { value: "CNO", label: "Cuanza-Norte" },
        { value: "CUS", label: "Cuanza-Sul" },
        { value: "HUA", label: "Huambo" },
        { value: "HUI", label: "Huíla" },
        { value: "LNO", label: "Lunda-Norte" },
        { value: "LSU", label: "Lunda-Sul" },
        { value: "LUA", label: "Luanda" },
        { value: "MAL", label: "Malanje" },
        { value: "MOX", label: "Moxico" },
        { value: "NAM", label: "Namibe" },
        { value: "UIG", label: "Uíge" },
        { value: "ZAI", label: "Zaire" }
    ],
    "MZ": [
        { value: "P", label: "Cabo Delgado" },
        { value: "G", label: "Gaza" },
        { value: "I", label: "Inhambane" },
        { value: "B", label: "Manica" },
        { value: "MPM", label: "Maputo" },
        { value: "L", label: "Maputo City" },
        { value: "N", label: "Nampula" },
        { value: "A", label: "Niassa" },
        { value: "S", label: "Sofala" },
        { value: "T", label: "Tete" },
        { value: "Q", label: "Zambézia" }
    ],
    "ZM": [
        { value: "01", label: "Central" },
        { value: "02", label: "Copperbelt" },
        { value: "03", label: "Eastern" },
        { value: "04", label: "Luapula" },
        { value: "05", label: "Lusaka" },
        { value: "06", label: "Northern" },
        { value: "07", label: "North-Western" },
        { value: "08", label: "Southern" },
        { value: "09", label: "Western" },
        { value: "10", label: "Muchinga" }
    ],
    "ZW": [
        { value: "BU", label: "Bulawayo" },
        { value: "HA", label: "Harare" },
        { value: "MA", label: "Manicaland" },
        { value: "MC", label: "Mashonaland Central" },
        { value: "ME", label: "Mashonaland East" },
        { value: "MI", label: "Midlands" },
        { value: "MN", label: "Matabeleland North" },
        { value: "MS", label: "Matabeleland South" },
        { value: "MV", label: "Masvingo" },
        { value: "MW", label: "Mashonaland West" }
    ],
    "MW": [
        { value: "BA", label: "Balaka" },
        { value: "BL", label: "Blantyre" },
        { value: "CK", label: "Chikwawa" },
        { value: "CR", label: "Chiradzulu" },
        { value: "CT", label: "Chitipa" },
        { value: "DE", label: "Dedza" },
        { value: "DO", label: "Dowa" },
        { value: "KR", label: "Karonga" },
        { value: "KS", label: "Kasungu" },
        { value: "LI", label: "Lilongwe" },
        { value: "LK", label: "Likoma" },
        { value: "MC", label: "Mchinji" },
        { value: "MG", label: "Mangochi" },
        { value: "MH", label: "Machinga" },
        { value: "MU", label: "Mulanje" },
        { value: "MW", label: "Mwanza" },
        { value: "MZ", label: "Mzimba" },
        { value: "NB", label: "Nkhata Bay" },
        { value: "NE", label: "Neno" },
        { value: "NI", label: "Ntchisi" },
        { value: "NK", label: "Nkhotakota" },
        { value: "NS", label: "Nsanje" },
        { value: "NT", label: "Ntcheu" },
        { value: "NU", label: "Ntcheu" },
        { value: "PH", label: "Phalombe" },
        { value: "RU", label: "Rumphi" },
        { value: "SA", label: "Salima" },
        { value: "TH", label: "Thyolo" },
        { value: "ZO", label: "Zomba" }
    ],
    "SS": [
        { value: "BN", label: "Northern Bahr el Ghazal" },
        { value: "BW", label: "Western Bahr el Ghazal" },
        { value: "EC", label: "Central Equatoria" },
        { value: "EE", label: "Eastern Equatoria" },
        { value: "EW", label: "Western Equatoria" },
        { value: "JG", label: "Jonglei" },
        { value: "LK", label: "Lakes" },
        { value: "NU", label: "Upper Nile" },
        { value: "UY", label: "Unity" },
        { value: "WR", label: "Warrap" }
    ],
    "SD": [
        { value: "DC", label: "Central Darfur" },
        { value: "DE", label: "East Darfur" },
        { value: "DN", label: "North Darfur" },
        { value: "DS", label: "South Darfur" },
        { value: "DW", label: "West Darfur" },
        { value: "GD", label: "Gedaref" },
        { value: "GK", label: "West Kordofan" },
        { value: "GZ", label: "Gezira" },
        { value: "KA", label: "Kassala" },
        { value: "KH", label: "Khartoum" },
        { value: "KN", label: "North Kordofan" },
        { value: "KS", label: "South Kordofan" },
        { value: "NB", label: "Blue Nile" },
        { value: "NO", label: "Northern" },
        { value: "NR", label: "River Nile" },
        { value: "NW", label: "White Nile" },
        { value: "RS", label: "Red Sea" },
        { value: "SI", label: "Sennar" }
    ],
    "CM": [
        { value: "AD", label: "Adamawa" },
        { value: "CE", label: "Centre" },
        { value: "EN", label: "Far North" },
        { value: "ES", label: "East" },
        { value: "LT", label: "Littoral" },
        { value: "NO", label: "North" },
        { value: "NW", label: "North-West" },
        { value: "OU", label: "West" },
        { value: "SU", label: "South" },
        { value: "SW", label: "South-West" }
    ],
    "ML": [
        { value: "BKO", label: "Bamako" },
        { value: "2", label: "Gao" },
        { value: "1", label: "Kayes" },
        { value: "3", label: "Kidal" },
        { value: "4", label: "Koulikoro" },
        { value: "5", label: "Mopti" },
        { value: "6", label: "Ségou" },
        { value: "7", label: "Sikasso" },
        { value: "8", label: "Tombouctou" },
        { value: "9", label: "Ménaka" },
        { value: "10", label: "Taoudénit" }
    ],
    "BF": [
        { value: "01", label: "Boucle du Mouhoun" },
        { value: "02", label: "Cascades" },
        { value: "03", label: "Centre" },
        { value: "04", label: "Centre-Est" },
        { value: "05", label: "Centre-Nord" },
        { value: "06", label: "Centre-Ouest" },
        { value: "07", label: "Centre-Sud" },
        { value: "08", label: "Est" },
        { value: "09", label: "Hauts-Bassins" },
        { value: "10", label: "Nord" },
        { value: "11", label: "Plateau-Central" },
        { value: "12", label: "Sahel" },
        { value: "13", label: "Sud-Ouest" }
    ],
    "NE": [
        { value: "1", label: "Agadez" },
        { value: "2", label: "Diffa" },
        { value: "3", label: "Dosso" },
        { value: "4", label: "Maradi" },
        { value: "5", label: "Niamey" },
        { value: "6", label: "Tahoua" },
        { value: "7", label: "Tillabéri" },
        { value: "8", label: "Zinder" }
    ],
    "GA": [
        { value: "1", label: "Estuaire" },
        { value: "2", label: "Haut-Ogooué" },
        { value: "3", label: "Moyen-Ogooué" },
        { value: "4", label: "Ngounié" },
        { value: "5", label: "Nyanga" },
        { value: "6", label: "Ogooué-Ivindo" },
        { value: "7", label: "Ogooué-Lolo" },
        { value: "8", label: "Ogooué-Maritime" },
        { value: "9", label: "Woleu-Ntem" }
    ],
    "CG": [
        { value: "11", label: "Bouenza" },
        { value: "8", label: "Cuvette" },
        { value: "15", label: "Cuvette-Ouest" },
        { value: "5", label: "Kouilou" },
        { value: "2", label: "Lékoumou" },
        { value: "7", label: "Likouala" },
        { value: "9", label: "Niari" },
        { value: "14", label: "Plateaux" },
        { value: "16", label: "Pointe-Noire" },
        { value: "12", label: "Pool" },
        { value: "13", label: "Sangha" },
        { value: "BZV", label: "Brazzaville" }
    ],
    "CF": [
        { value: "AC", label: "Ouham" },
        { value: "BB", label: "Bamingui-Bangoran" },
        { value: "BGF", label: "Bangui" },
        { value: "BK", label: "Basse-Kotto" },
        { value: "HK", label: "Haute-Kotto" },
        { value: "HM", label: "Haut-Mbomou" },
        { value: "HS", label: "Haute-Sangha" },
        { value: "KB", label: "Gribingui" },
        { value: "KG", label: "Kémo-Gribingui" },
        { value: "LB", label: "Lobaye" },
        { value: "MB", label: "Mbomou" },
        { value: "MP", label: "Ombella-Mpoko" },
        { value: "NM", label: "Nana-Mambéré" },
        { value: "OP", label: "Ouham-Pendé" },
        { value: "SE", label: "Sangha" },
        { value: "UK", label: "Ouaka" },
        { value: "VK", label: "Vakaga" }
    ],
    "NA": [
        { value: "CA", label: "Zambezi" },
        { value: "ER", label: "Erongo" },
        { value: "HA", label: "Hardap" },
        { value: "KA", label: "Karas" },
        { value: "KE", label: "Kavango East" },
        { value: "KH", label: "Khomas" },
        { value: "KU", label: "Kunene" },
        { value: "KW", label: "Kavango West" },
        { value: "OD", label: "Otjozondjupa" },
        { value: "OH", label: "Omaheke" },
        { value: "ON", label: "Oshana" },
        { value: "OS", label: "Omusati" },
        { value: "OT", label: "Oshikoto" },
        { value: "OW", label: "Ohangwena" }
    ],
    "BW": [
        { value: "CE", label: "Central" },
        { value: "CH", label: "Chobe" },
        { value: "FR", label: "Francistown" },
        { value: "GA", label: "Gaborone" },
        { value: "GH", label: "Ghanzi" },
        { value: "JW", label: "Jwaneng" },
        { value: "KG", label: "Kgalagadi" },
        { value: "KL", label: "Kgatleng" },
        { value: "KW", label: "Kweneng" },
        { value: "LO", label: "Lobatse" },
        { value: "NE", label: "North East" },
        { value: "NW", label: "North West" },
        { value: "SE", label: "South East" },
        { value: "SO", label: "Southern" },
        { value: "SP", label: "Selibe Phikwe" },
        { value: "ST", label: "Sowa Town" }
    ],
    "LS": [
        { value: "A", label: "Maseru" },
        { value: "B", label: "Butha-Buthe" },
        { value: "C", label: "Leribe" },
        { value: "D", label: "Berea" },
        { value: "E", label: "Mafeteng" },
        { value: "F", label: "Mohale's Hoek" },
        { value: "G", label: "Quthing" },
        { value: "H", label: "Qacha's Nek" },
        { value: "J", label: "Mokhotlong" },
        { value: "K", label: "Thaba-Tseka" }
    ],
    "SZ": [
        { value: "HH", label: "Hhohho" },
        { value: "LU", label: "Lubombo" },
        { value: "MA", label: "Manzini" },
        { value: "SH", label: "Shiselweni" }
    ],
    "MG": [
        { value: "T", label: "Antananarivo" },
        { value: "D", label: "Antsiranana" },
        { value: "F", label: "Fianarantsoa" },
        { value: "M", label: "Mahajanga" },
        { value: "A", label: "Toamasina" },
        { value: "U", label: "Toliara" }
    ],
    "MU": [
        { value: "AG", label: "Agalega Islands" },
        { value: "BL", label: "Black River" },
        { value: "CC", label: "Cargados Carajos" },
        { value: "FL", label: "Flacq" },
        { value: "GP", label: "Grand Port" },
        { value: "MO", label: "Moka" },
        { value: "PA", label: "Pamplemousses" },
        { value: "PL", label: "Port Louis" },
        { value: "PW", label: "Plaines Wilhems" },
        { value: "RO", label: "Rodrigues" },
        { value: "RR", label: "Rivière du Rempart" },
        { value: "SA", label: "Savanne" }
    ],
    "SC": [
        { value: "01", label: "Anse aux Pins" },
        { value: "02", label: "Anse Boileau" },
        { value: "03", label: "Anse Etoile" },
        { value: "04", label: "Anse Royale" },
        { value: "05", label: "Anse-aux-Pins" },
        { value: "06", label: "Au Cap" },
        { value: "07", label: "Baie Lazare" },
        { value: "08", label: "Baie Sainte Anne" },
        { value: "09", label: "Beau Vallon" },
        { value: "10", label: "Bel Air" },
        { value: "11", label: "Bel Ombre" },
        { value: "12", label: "Cascade" },
        { value: "13", label: "Glacis" },
        { value: "14", label: "Grand'Anse Mahé" },
        { value: "15", label: "Grand'Anse Praslin" },
        { value: "16", label: "La Digue" },
        { value: "17", label: "La Rivière Anglaise" },
        { value: "18", label: "Les Mamelles" },
        { value: "19", label: "Mont Buxton" },
        { value: "20", label: "Mont Fleuri" },
        { value: "21", label: "Plaisance" },
        { value: "22", label: "Pointe La Rue" },
        { value: "23", label: "Port Glaud" },
        { value: "24", label: "Roche Caiman" },
        { value: "25", label: "Saint Louis" },
        { value: "26", label: "Takamaka" }
    ],
    "SO": [
        { value: "AW", label: "Awdal" },
        { value: "BK", label: "Bakool" },
        { value: "BN", label: "Banaadir" },
        { value: "BR", label: "Bari" },
        { value: "BY", label: "Bay" },
        { value: "GA", label: "Galguduud" },
        { value: "GE", label: "Gedo" },
        { value: "HI", label: "Hiiraan" },
        { value: "JD", label: "Middle Juba" },
        { value: "JH", label: "Lower Juba" },
        { value: "MU", label: "Mudug" },
        { value: "NU", label: "Nugaal" },
        { value: "SA", label: "Sanaag" },
        { value: "SD", label: "Middle Shabelle" },
        { value: "SH", label: "Lower Shabelle" },
        { value: "SO", label: "Sool" },
        { value: "TO", label: "Togdheer" },
        { value: "WO", label: "Woqooyi Galbeed" }
    ],
    "ER": [
        { value: "AN", label: "Anseba" },
        { value: "DK", label: "Southern Red Sea" },
        { value: "DU", label: "Debub" },
        { value: "GB", label: "Gash-Barka" },
        { value: "MA", label: "Maekel" },
        { value: "SK", label: "Northern Red Sea" }
    ],
    "DJ": [
        { value: "AR", label: "Arta" },
        { value: "AS", label: "Ali Sabieh" },
        { value: "DI", label: "Dikhil" },
        { value: "DJ", label: "Djibouti" },
        { value: "OB", label: "Obock" },
        { value: "TA", label: "Tadjourah" }
    ],
    "LY": [
        { value: "BU", label: "Butnan" },
        { value: "DR", label: "Derna" },
        { value: "GT", label: "Ghat" },
        { value: "JA", label: "Jabal al Akhdar" },
        { value: "JG", label: "Jabal al Gharbi" },
        { value: "JI", label: "Jafara" },
        { value: "JU", label: "Jufra" },
        { value: "KF", label: "Kufra" },
        { value: "MB", label: "Murqub" },
        { value: "MI", label: "Misrata" },
        { value: "MJ", label: "Marj" },
        { value: "MQ", label: "Murzuq" },
        { value: "NL", label: "Nalut" },
        { value: "NQ", label: "Nuqat al Khams" },
        { value: "SB", label: "Sabha" },
        { value: "SR", label: "Sirte" },
        { value: "TB", label: "Tripoli" },
        { value: "WA", label: "Wadi al Hayaa" },
        { value: "WD", label: "Wadi al Shatii" },
        { value: "WS", label: "Waw al Kabir" },
        { value: "ZA", label: "Zawiya" },
        { value: "ZW", label: "Az Zawiyah" }
    ],
    "TD": [
        { value: "BA", label: "Batha" },
        { value: "BG", label: "Barh el Gazel" },
        { value: "BO", label: "Borkou" },
        { value: "CB", label: "Chari-Baguirmi" },
        { value: "EE", label: "Ennedi-Est" },
        { value: "EO", label: "Ennedi-Ouest" },
        { value: "GR", label: "Guéra" },
        { value: "HL", label: "Hadjer-Lamis" },
        { value: "KA", label: "Kanem" },
        { value: "LC", label: "Lac" },
        { value: "LO", label: "Logone Occidental" },
        { value: "LR", label: "Logone Oriental" },
        { value: "MA", label: "Mandoul" },
        { value: "MC", label: "Moyen-Chari" },
        { value: "ME", label: "Mayo-Kebbi Est" },
        { value: "MO", label: "Mayo-Kebbi Ouest" },
        { value: "ND", label: "N'Djamena" },
        { value: "OD", label: "Ouaddaï" },
        { value: "SA", label: "Salamat" },
        { value: "SI", label: "Sila" },
        { value: "TA", label: "Tandjilé" },
        { value: "TI", label: "Tibesti" },
        { value: "WF", label: "Wadi Fira" }
    ],
    "CU": [
        { value: "01", label: "Pinar del Río" },
        { value: "03", label: "Artemisa" },
        { value: "04", label: "Havana" },
        { value: "05", label: "Mayabeque" },
        { value: "06", label: "Matanzas" },
        { value: "07", label: "Cienfuegos" },
        { value: "08", label: "Villa Clara" },
        { value: "09", label: "Sancti Spíritus" },
        { value: "10", label: "Ciego de Ávila" },
        { value: "11", label: "Camagüey" },
        { value: "12", label: "Las Tunas" },
        { value: "13", label: "Holguín" },
        { value: "14", label: "Granma" },
        { value: "15", label: "Santiago de Cuba" },
        { value: "16", label: "Guantánamo" },
        { value: "99", label: "Isla de la Juventud" }
    ],
    "JM": [
        { value: "01", label: "Kingston" },
        { value: "02", label: "Saint Andrew" },
        { value: "03", label: "Saint Thomas" },
        { value: "04", label: "Portland" },
        { value: "05", label: "Saint Mary" },
        { value: "06", label: "Saint Ann" },
        { value: "07", label: "Trelawny" },
        { value: "08", label: "Saint James" },
        { value: "09", label: "Hanover" },
        { value: "10", label: "Westmoreland" },
        { value: "11", label: "Saint Elizabeth" },
        { value: "12", label: "Manchester" },
        { value: "13", label: "Clarendon" },
        { value: "14", label: "Saint Catherine" }
    ],
    "HT": [
        { value: "AR", label: "Artibonite" },
        { value: "CE", label: "Centre" },
        { value: "GA", label: "Grande'Anse" },
        { value: "ND", label: "Nord" },
        { value: "NE", label: "Nord-Est" },
        { value: "NI", label: "Nippes" },
        { value: "NO", label: "Nord-Ouest" },
        { value: "OU", label: "Ouest" },
        { value: "SD", label: "Sud" },
        { value: "SE", label: "Sud-Est" }
    ],
    "TT": [
        { value: "ARI", label: "Arima" },
        { value: "CHA", label: "Chaguanas" },
        { value: "CTT", label: "Couva-Tabaquite-Talparo" },
        { value: "DMN", label: "Diego Martin" },
        { value: "MRC", label: "Mayaro-Rio Claro" },
        { value: "PED", label: "Penal-Debe" },
        { value: "POS", label: "Port of Spain" },
        { value: "PRT", label: "Princes Town" },
        { value: "PTF", label: "Point Fortin" },
        { value: "SFO", label: "San Fernando" },
        { value: "SGE", label: "Sangre Grande" },
        { value: "SIP", label: "Siparia" },
        { value: "SJL", label: "San Juan-Laventille" },
        { value: "TOB", label: "Tobago" },
        { value: "TUP", label: "Tunapuna-Piarco" }
    ],
    "BS": [
        { value: "AK", label: "Acklins" },
        { value: "BI", label: "Bimini" },
        { value: "BP", label: "Black Point" },
        { value: "BY", label: "Berry Islands" },
        { value: "CE", label: "Central Eleuthera" },
        { value: "CI", label: "Cat Island" },
        { value: "CK", label: "Crooked Island and Long Cay" },
        { value: "CO", label: "Central Abaco" },
        { value: "CS", label: "Central Andros" },
        { value: "EG", label: "East Grand Bahama" },
        { value: "EX", label: "Exuma" },
        { value: "FP", label: "Freeport" },
        { value: "GC", label: "Grand Cay" },
        { value: "HI", label: "Harbour Island" },
        { value: "HT", label: "Hope Town" },
        { value: "IN", label: "Inagua" },
        { value: "LI", label: "Long Island" },
        { value: "MC", label: "Mangrove Cay" },
        { value: "MG", label: "Mayaguana" },
        { value: "MI", label: "Moore's Island" },
        { value: "NE", label: "North Eleuthera" },
        { value: "NO", label: "North Abaco" },
        { value: "NP", label: "New Providence" },
        { value: "NS", label: "North Andros" },
        { value: "RC", label: "Rum Cay" },
        { value: "RI", label: "Ragged Island" },
        { value: "SA", label: "South Andros" },
        { value: "SE", label: "South Eleuthera" },
        { value: "SO", label: "South Abaco" },
        { value: "SS", label: "San Salvador" },
        { value: "SW", label: "Spanish Wells" },
        { value: "WG", label: "West Grand Bahama" }
    ],
    "BB": [
        { value: "01", label: "Christ Church" },
        { value: "02", label: "Saint Andrew" },
        { value: "03", label: "Saint George" },
        { value: "04", label: "Saint James" },
        { value: "05", label: "Saint John" },
        { value: "06", label: "Saint Joseph" },
        { value: "07", label: "Saint Lucy" },
        { value: "08", label: "Saint Michael" },
        { value: "09", label: "Saint Peter" },
        { value: "10", label: "Saint Philip" },
        { value: "11", label: "Saint Thomas" }
    ],
    "SR": [
        { value: "BR", label: "Brokopondo" },
        { value: "CM", label: "Commewijne" },
        { value: "CR", label: "Coronie" },
        { value: "MA", label: "Marowijne" },
        { value: "NI", label: "Nickerie" },
        { value: "PA", label: "Para" },
        { value: "PM", label: "Paramaribo" },
        { value: "SA", label: "Saramacca" },
        { value: "SI", label: "Sipaliwini" },
        { value: "WA", label: "Wanica" }
    ],
    "GY": [
        { value: "BA", label: "Barima-Waini" },
        { value: "CU", label: "Cuyuni-Mazaruni" },
        { value: "DE", label: "Demerara-Mahaica" },
        { value: "EB", label: "East Berbice-Corentyne" },
        { value: "ES", label: "Essequibo Islands-West Demerara" },
        { value: "MA", label: "Mahaica-Berbice" },
        { value: "PM", label: "Pomeroon-Supenaam" },
        { value: "PT", label: "Potaro-Siparuni" },
        { value: "UD", label: "Upper Demerara-Berbice" },
        { value: "UT", label: "Upper Takutu-Upper Essequibo" }
    ],
    "BZ": [
        { value: "BZ", label: "Belize" },
        { value: "CY", label: "Cayo" },
        { value: "CZL", label: "Corozal" },
        { value: "OW", label: "Orange Walk" },
        { value: "SC", label: "Stann Creek" },
        { value: "TOL", label: "Toledo" }
    ],
    "AG": [
        { value: "03", label: "Saint George" },
        { value: "04", label: "Saint John" },
        { value: "05", label: "Saint Mary" },
        { value: "06", label: "Saint Paul" },
        { value: "07", label: "Saint Peter" },
        { value: "08", label: "Saint Philip" },
        { value: "10", label: "Barbuda" },
        { value: "11", label: "Redonda" }
    ],
    "DM": [
        { value: "02", label: "Saint Andrew" },
        { value: "03", label: "Saint David" },
        { value: "04", label: "Saint George" },
        { value: "05", label: "Saint John" },
        { value: "06", label: "Saint Joseph" },
        { value: "07", label: "Saint Luke" },
        { value: "08", label: "Saint Mark" },
        { value: "09", label: "Saint Patrick" },
        { value: "10", label: "Saint Paul" },
        { value: "11", label: "Saint Peter" }
    ],
    "GD": [
        { value: "01", label: "Saint Andrew" },
        { value: "02", label: "Saint David" },
        { value: "03", label: "Saint George" },
        { value: "04", label: "Saint John" },
        { value: "05", label: "Saint Mark" },
        { value: "06", label: "Saint Patrick" },
        { value: "10", label: "Southern Grenadine Islands" }
    ],
    "KN": [
        { value: "01", label: "Christ Church Nichola Town" },
        { value: "02", label: "Saint Anne Sandy Point" },
        { value: "03", label: "Saint George Basseterre" },
        { value: "04", label: "Saint George Gingerland" },
        { value: "05", label: "Saint James Windward" },
        { value: "06", label: "Saint John Capisterre" },
        { value: "07", label: "Saint John Figtree" },
        { value: "08", label: "Saint Mary Cayon" },
        { value: "09", label: "Saint Paul Capisterre" },
        { value: "10", label: "Saint Paul Charlestown" },
        { value: "11", label: "Saint Peter Basseterre" },
        { value: "12", label: "Saint Thomas Lowland" },
        { value: "13", label: "Saint Thomas Middle Island" },
        { value: "15", label: "Trinity Palmetto Point" }
    ],
    "LC": [
        { value: "01", label: "Anse la Raye" },
        { value: "02", label: "Canaries" },
        { value: "03", label: "Castries" },
        { value: "04", label: "Choiseul" },
        { value: "05", label: "Dennery" },
        { value: "06", label: "Gros Islet" },
        { value: "07", label: "Laborie" },
        { value: "08", label: "Micoud" },
        { value: "09", label: "Soufrière" },
        { value: "10", label: "Vieux Fort" }
    ],
    "VC": [
        { value: "01", label: "Charlotte" },
        { value: "02", label: "Grenadines" },
        { value: "03", label: "Saint Andrew" },
        { value: "04", label: "Saint David" },
        { value: "05", label: "Saint George" },
        { value: "06", label: "Saint Patrick" }
    ],
    "AF": [
        { value: "BAL", label: "Balkh" },
        { value: "BAM", label: "Bamyan" },
        { value: "BDG", label: "Badghis" },
        { value: "BDS", label: "Badakhshan" },
        { value: "BGL", label: "Baghlan" },
        { value: "DAY", label: "Daykundi" },
        { value: "FRA", label: "Farah" },
        { value: "FYB", label: "Faryab" },
        { value: "GHA", label: "Ghazni" },
        { value: "GHO", label: "Ghor" },
        { value: "HEL", label: "Helmand" },
        { value: "HER", label: "Herat" },
        { value: "JOW", label: "Jowzjan" },
        { value: "KAB", label: "Kabul" },
        { value: "KAN", label: "Kandahar" },
        { value: "KAP", label: "Kapisa" },
        { value: "KDZ", label: "Kunduz" },
        { value: "KHO", label: "Khost" },
        { value: "KNR", label: "Kunar" },
        { value: "LAG", label: "Laghman" },
        { value: "LOG", label: "Logar" },
        { value: "NAN", label: "Nangarhar" },
        { value: "NIM", label: "Nimruz" },
        { value: "NUR", label: "Nuristan" },
        { value: "PAN", label: "Panjshir" },
        { value: "PAR", label: "Parwan" },
        { value: "PIA", label: "Paktia" },
        { value: "PKA", label: "Paktika" },
        { value: "SAM", label: "Samangan" },
        { value: "SAR", label: "Sar-e Pol" },
        { value: "TAK", label: "Takhar" },
        { value: "URU", label: "Uruzgan" },
        { value: "WAR", label: "Wardak" },
        { value: "ZAB", label: "Zabul" }
    ],
    "BT": [
        { value: "11", label: "Paro" },
        { value: "12", label: "Chhukha" },
        { value: "13", label: "Haa" },
        { value: "14", label: "Samtse" },
        { value: "15", label: "Thimphu" },
        { value: "21", label: "Tsirang" },
        { value: "22", label: "Dagana" },
        { value: "23", label: "Punakha" },
        { value: "24", label: "Wangdue Phodrang" },
        { value: "31", label: "Sarpang" },
        { value: "32", label: "Trongsa" },
        { value: "33", label: "Bumthang" },
        { value: "34", label: "Zhemgang" },
        { value: "41", label: "Trashigang" },
        { value: "42", label: "Monggar" },
        { value: "43", label: "Pemagatshel" },
        { value: "44", label: "Lhuntse" },
        { value: "45", label: "Samdrup Jongkhar" },
        { value: "GA", label: "Gasa" },
        { value: "TY", label: "Trashi Yangtse" }
    ],
    "BN": [
        { value: "BE", label: "Belait" },
        { value: "BM", label: "Brunei-Muara" },
        { value: "TE", label: "Temburong" },
        { value: "TU", label: "Tutong" }
    ],
    "MV": [
        { value: "CE", label: "Central" },
        { value: "MLE", label: "Male" },
        { value: "NC", label: "North Central" },
        { value: "NO", label: "North" },
        { value: "SC", label: "South Central" },
        { value: "SU", label: "South" },
        { value: "UN", label: "Upper North" },
        { value: "US", label: "Upper South" }
    ],
    "TL": [
        { value: "AL", label: "Aileu" },
        { value: "AN", label: "Ainaro" },
        { value: "BA", label: "Baucau" },
        { value: "BO", label: "Bobonaro" },
        { value: "CO", label: "Cova Lima" },
        { value: "DI", label: "Dili" },
        { value: "ER", label: "Ermera" },
        { value: "LA", label: "Lautem" },
        { value: "LI", label: "Liquiça" },
        { value: "MF", label: "Manufahi" },
        { value: "MT", label: "Manatuto" },
        { value: "OE", label: "Oecusse" },
        { value: "VI", label: "Viqueque" }
    ],
    "KP": [
        { value: "01", label: "Pyongyang" },
        { value: "02", label: "South Pyongan" },
        { value: "03", label: "North Pyongan" },
        { value: "04", label: "Chagang" },
        { value: "05", label: "South Hwanghae" },
        { value: "06", label: "North Hwanghae" },
        { value: "07", label: "Kangwon" },
        { value: "08", label: "South Hamgyong" },
        { value: "09", label: "North Hamgyong" },
        { value: "10", label: "Ryanggang" }
    ],
    "PG": [
        { value: "CPK", label: "Central" },
        { value: "CPM", label: "Chimbu" },
        { value: "EBR", label: "East New Britain" },
        { value: "EHG", label: "Eastern Highlands" },
        { value: "EPW", label: "Enga" },
        { value: "ESW", label: "East Sepik" },
        { value: "GPK", label: "Gulf" },
        { value: "HLA", label: "Hela" },
        { value: "JWK", label: "Jiwaka" },
        { value: "MBA", label: "Milne Bay" },
        { value: "MPL", label: "Morobe" },
        { value: "MPM", label: "Madang" },
        { value: "MRL", label: "Manus" },
        { value: "NCD", label: "National Capital District" },
        { value: "NIK", label: "New Ireland" },
        { value: "NPP", label: "Northern" },
        { value: "NSB", label: "Bougainville" },
        { value: "SAN", label: "Sandaun" },
        { value: "SHM", label: "Southern Highlands" },
        { value: "WBK", label: "West New Britain" },
        { value: "WHM", label: "Western Highlands" },
        { value: "WPD", label: "Western" }
    ],
    "FJ": [
        { value: "C", label: "Central" },
        { value: "E", label: "Eastern" },
        { value: "N", label: "Northern" },
        { value: "R", label: "Rotuma" },
        { value: "W", label: "Western" }
    ],
    "IS": [
        { value: "1", label: "Capital Region" },
        { value: "1-IS", label: "Höfuðborgarsvæðið", canonicalName: "Capital Region" }, // Icelandic native (Reykjavik region)
        { value: "2", label: "Southern Peninsula" },
        { value: "3", label: "Western Region" },
        { value: "4", label: "Westfjords" },
        { value: "5", label: "Northwestern Region" },
        { value: "6", label: "Northeastern Region" },
        { value: "7", label: "Eastern Region" },
        { value: "8", label: "Southern Region" }
    ],
    "XK": [
        { value: "01", label: "Pristina" },
        { value: "02", label: "Mitrovica" },
        { value: "03", label: "Peja" },
        { value: "04", label: "Prizren" },
        { value: "05", label: "Ferizaj" },
        { value: "06", label: "Gjilan" },
        { value: "07", label: "Gjakova" }
    ],
    "PR": [
        { value: "001", label: "Adjuntas" },
        { value: "003", label: "Aguada" },
        { value: "005", label: "Aguadilla" },
        { value: "007", label: "Aguas Buenas" },
        { value: "009", label: "Aibonito" },
        { value: "011", label: "Añasco" },
        { value: "013", label: "Arecibo" },
        { value: "015", label: "Arroyo" },
        { value: "017", label: "Barceloneta" },
        { value: "019", label: "Barranquitas" },
        { value: "021", label: "Bayamón" },
        { value: "023", label: "Cabo Rojo" },
        { value: "025", label: "Caguas" },
        { value: "027", label: "Camuy" },
        { value: "029", label: "Canóvanas" },
        { value: "031", label: "Carolina" },
        { value: "033", label: "Cataño" },
        { value: "035", label: "Cayey" },
        { value: "037", label: "Ceiba" },
        { value: "039", label: "Ciales" },
        { value: "041", label: "Cidra" },
        { value: "043", label: "Coamo" },
        { value: "045", label: "Comerío" },
        { value: "047", label: "Corozal" },
        { value: "049", label: "Culebra" },
        { value: "051", label: "Dorado" },
        { value: "053", label: "Fajardo" },
        { value: "054", label: "Florida" },
        { value: "055", label: "Guánica" },
        { value: "057", label: "Guayama" },
        { value: "059", label: "Guayanilla" },
        { value: "061", label: "Guaynabo" },
        { value: "063", label: "Gurabo" },
        { value: "065", label: "Hatillo" },
        { value: "067", label: "Hormigueros" },
        { value: "069", label: "Humacao" },
        { value: "071", label: "Isabela" },
        { value: "073", label: "Jayuya" },
        { value: "075", label: "Juana Díaz" },
        { value: "077", label: "Juncos" },
        { value: "079", label: "Lajas" },
        { value: "081", label: "Lares" },
        { value: "083", label: "Las Marías" },
        { value: "085", label: "Las Piedras" },
        { value: "087", label: "Loíza" },
        { value: "089", label: "Luquillo" },
        { value: "091", label: "Manatí" },
        { value: "093", label: "Maricao" },
        { value: "095", label: "Maunabo" },
        { value: "097", label: "Mayagüez" },
        { value: "099", label: "Moca" },
        { value: "101", label: "Morovis" },
        { value: "103", label: "Naguabo" },
        { value: "105", label: "Naranjito" },
        { value: "107", label: "Orocovis" },
        { value: "109", label: "Patillas" },
        { value: "111", label: "Peñuelas" },
        { value: "113", label: "Ponce" },
        { value: "115", label: "Quebradillas" },
        { value: "117", label: "Rincón" },
        { value: "119", label: "Río Grande" },
        { value: "121", label: "Sabana Grande" },
        { value: "123", label: "Salinas" },
        { value: "125", label: "San Germán" },
        { value: "127", label: "San Juan" },
        { value: "129", label: "San Lorenzo" },
        { value: "131", label: "San Sebastián" },
        { value: "133", label: "Santa Isabel" },
        { value: "135", label: "Toa Alta" },
        { value: "137", label: "Toa Baja" },
        { value: "139", label: "Trujillo Alto" },
        { value: "141", label: "Utuado" },
        { value: "143", label: "Vega Alta" },
        { value: "145", label: "Vega Baja" },
        { value: "147", label: "Vieques" },
        { value: "149", label: "Villalba" },
        { value: "151", label: "Yabucoa" },
        { value: "153", label: "Yauco" }
    ],
    "HK": [
        { value: "HCW", label: "Central and Western" },
        { value: "HEA", label: "Eastern" },
        { value: "HSO", label: "Southern" },
        { value: "HWC", label: "Wan Chai" },
        { value: "KKC", label: "Kowloon City" },
        { value: "KKT", label: "Kwun Tong" },
        { value: "KSS", label: "Sham Shui Po" },
        { value: "KWT", label: "Wong Tai Sin" },
        { value: "KYT", label: "Yau Tsim Mong" },
        { value: "NIS", label: "Islands" },
        { value: "NKT", label: "Kwai Tsing" },
        { value: "NNO", label: "North" },
        { value: "NSK", label: "Sai Kung" },
        { value: "NST", label: "Sha Tin" },
        { value: "NTA", label: "Tai Po" },
        { value: "NTW", label: "Tsuen Wan" },
        { value: "NTM", label: "Tuen Mun" },
        { value: "NYL", label: "Yuen Long" }
    ],
    "WS": [
        { value: "AA", label: "A'ana" },
        { value: "AL", label: "Aiga-i-le-Tai" },
        { value: "AT", label: "Atua" },
        { value: "FA", label: "Fa'asaleleaga" },
        { value: "GE", label: "Gaga'emauga" },
        { value: "GI", label: "Gagaifomauga" },
        { value: "PA", label: "Palauli" },
        { value: "SA", label: "Satupa'itea" },
        { value: "TU", label: "Tuamasaga" },
        { value: "VF", label: "Va'a-o-Fonoti" },
        { value: "VS", label: "Vaisigano" }
    ],
    "TO": [
        { value: "01", label: "Eua" },
        { value: "02", label: "Ha'apai" },
        { value: "03", label: "Niuas" },
        { value: "04", label: "Tongatapu" },
        { value: "05", label: "Vava'u" }
    ],
    "SB": [
        { value: "CE", label: "Central" },
        { value: "CH", label: "Choiseul" },
        { value: "CT", label: "Capital Territory" },
        { value: "GU", label: "Guadalcanal" },
        { value: "IS", label: "Isabel" },
        { value: "MK", label: "Makira-Ulawa" },
        { value: "ML", label: "Malaita" },
        { value: "RB", label: "Rennell and Bellona" },
        { value: "TE", label: "Temotu" },
        { value: "WE", label: "Western" }
    ],
    "VU": [
        { value: "MAP", label: "Malampa" },
        { value: "PAM", label: "Penama" },
        { value: "SAM", label: "Sanma" },
        { value: "SEE", label: "Shefa" },
        { value: "TAE", label: "Tafea" },
        { value: "TOB", label: "Torba" }
    ],
    "KI": [
        { value: "G", label: "Gilbert Islands" },
        { value: "L", label: "Line Islands" },
        { value: "P", label: "Phoenix Islands" }
    ],
    "MH": [
        { value: "L", label: "Ralik Chain" },
        { value: "T", label: "Ratak Chain" }
    ],
    "FM": [
        { value: "KSA", label: "Kosrae" },
        { value: "PNI", label: "Pohnpei" },
        { value: "TRK", label: "Chuuk" },
        { value: "YAP", label: "Yap" }
    ],
    "PW": [
        { value: "002", label: "Aimeliik" },
        { value: "004", label: "Airai" },
        { value: "010", label: "Angaur" },
        { value: "050", label: "Hatohobei" },
        { value: "100", label: "Kayangel" },
        { value: "150", label: "Koror" },
        { value: "212", label: "Melekeok" },
        { value: "214", label: "Ngaraard" },
        { value: "218", label: "Ngarchelong" },
        { value: "222", label: "Ngardmau" },
        { value: "224", label: "Ngatpang" },
        { value: "226", label: "Ngchesar" },
        { value: "227", label: "Ngeremlengui" },
        { value: "228", label: "Ngiwal" },
        { value: "350", label: "Peleliu" },
        { value: "370", label: "Sonsorol" }
    ],
    "NR": [
        { value: "01", label: "Aiwo" },
        { value: "02", label: "Anabar" },
        { value: "03", label: "Anetan" },
        { value: "04", label: "Anibare" },
        { value: "05", label: "Baiti" },
        { value: "06", label: "Boe" },
        { value: "07", label: "Buada" },
        { value: "08", label: "Denigomodu" },
        { value: "09", label: "Ewa" },
        { value: "10", label: "Ijuw" },
        { value: "11", label: "Meneng" },
        { value: "12", label: "Nibok" },
        { value: "13", label: "Uaboe" },
        { value: "14", label: "Yaren" }
    ],
    "TV": [
        { value: "FUN", label: "Funafuti" },
        { value: "NIT", label: "Niutao" },
        { value: "NKF", label: "Nukufetau" },
        { value: "NKL", label: "Nukulaelae" },
        { value: "NMA", label: "Nanumea" },
        { value: "NMG", label: "Nanumanga" },
        { value: "NUI", label: "Nui" },
        { value: "VAI", label: "Vaitupu" }
    ],
    "AD": [
        { value: "02", label: "Canillo" },
        { value: "03", label: "Encamp" },
        { value: "04", label: "La Massana" },
        { value: "05", label: "Ordino" },
        { value: "06", label: "Sant Julià de Lòria" },
        { value: "07", label: "Andorra la Vella" },
        { value: "08", label: "Escaldes-Engordany" }
    ],
    "LI": [
        { value: "01", label: "Balzers" },
        { value: "02", label: "Eschen" },
        { value: "03", label: "Gamprin" },
        { value: "04", label: "Mauren" },
        { value: "05", label: "Planken" },
        { value: "06", label: "Ruggell" },
        { value: "07", label: "Schaan" },
        { value: "08", label: "Schellenberg" },
        { value: "09", label: "Triesen" },
        { value: "10", label: "Triesenberg" },
        { value: "11", label: "Vaduz" }
    ],
    "MC": [
        { value: "CL", label: "La Colle" },
        { value: "CO", label: "La Condamine" },
        { value: "FO", label: "Fontvieille" },
        { value: "GA", label: "La Gare" },
        { value: "JE", label: "Jardin Exotique" },
        { value: "LA", label: "Larvotto" },
        { value: "MA", label: "Malbousquet" },
        { value: "MC", label: "Monte-Carlo" },
        { value: "MG", label: "Moneghetti" },
        { value: "MO", label: "Monaco-Ville" },
        { value: "MU", label: "Moulins" },
        { value: "PH", label: "Port-Hercule" },
        { value: "SD", label: "Sainte-Dévote" },
        { value: "SO", label: "La Source" },
        { value: "SP", label: "Spélugues" },
        { value: "SR", label: "Saint-Roman" },
        { value: "VR", label: "Vallon de la Rousse" }
    ],
    "SM": [
        { value: "01", label: "Acquaviva" },
        { value: "02", label: "Borgo Maggiore" },
        { value: "03", label: "Chiesanuova" },
        { value: "04", label: "Domagnano" },
        { value: "05", label: "Faetano" },
        { value: "06", label: "Fiorentino" },
        { value: "07", label: "Montegiardino" },
        { value: "08", label: "San Marino" },
        { value: "09", label: "Serravalle" }
    ],
    "MO": [
        { value: "MO", label: "Macau (single administrative region)" }
    ]
};

// Comprehensive country options (alphabetical by label)
export const countryOptions = [
    { value: "AF", label: "Afghanistan" },
    { value: "AL", label: "Albania" },
    { value: "DZ", label: "Algeria" },
    { value: "AD", label: "Andorra" },
    { value: "AO", label: "Angola" },
    { value: "AG", label: "Antigua and Barbuda" },
    { value: "AR", label: "Argentina" },
    { value: "AM", label: "Armenia" },
    { value: "AU", label: "Australia" },
    { value: "AT", label: "Austria" },
    { value: "AZ", label: "Azerbaijan" },
    { value: "BS", label: "Bahamas" },
    { value: "BH", label: "Bahrain" },
    { value: "BD", label: "Bangladesh" },
    { value: "BB", label: "Barbados" },
    { value: "BY", label: "Belarus" },
    { value: "BE", label: "Belgium" },
    { value: "BZ", label: "Belize" },
    { value: "BJ", label: "Benin" },
    { value: "BT", label: "Bhutan" },
    { value: "BO", label: "Bolivia" },
    { value: "BA", label: "Bosnia and Herzegovina" },
    { value: "BW", label: "Botswana" },
    { value: "BR", label: "Brazil" },
    { value: "BN", label: "Brunei" },
    { value: "BG", label: "Bulgaria" },
    { value: "BF", label: "Burkina Faso" },
    { value: "BI", label: "Burundi" },
    { value: "KH", label: "Cambodia" },
    { value: "CM", label: "Cameroon" },
    { value: "CA", label: "Canada" },
    { value: "CV", label: "Cape Verde" },
    { value: "CF", label: "Central African Republic" },
    { value: "TD", label: "Chad" },
    { value: "CL", label: "Chile" },
    { value: "CN", label: "China" },
    { value: "CO", label: "Colombia" },
    { value: "KM", label: "Comoros" },
    { value: "CR", label: "Costa Rica" },
    { value: "CI", label: "Côte d'Ivoire" },
    { value: "HR", label: "Croatia" },
    { value: "CU", label: "Cuba" },
    { value: "CY", label: "Cyprus" },
    { value: "CZ", label: "Czech Republic" },
    { value: "CD", label: "Democratic Republic of the Congo" },
    { value: "DK", label: "Denmark" },
    { value: "DJ", label: "Djibouti" },
    { value: "DM", label: "Dominica" },
    { value: "DO", label: "Dominican Republic" },
    { value: "EC", label: "Ecuador" },
    { value: "EG", label: "Egypt" },
    { value: "SV", label: "El Salvador" },
    { value: "GQ", label: "Equatorial Guinea" },
    { value: "ER", label: "Eritrea" },
    { value: "EE", label: "Estonia" },
    { value: "SZ", label: "Eswatini" },
    { value: "ET", label: "Ethiopia" },
    { value: "FJ", label: "Fiji" },
    { value: "FI", label: "Finland" },
    { value: "FR", label: "France" },
    { value: "GA", label: "Gabon" },
    { value: "GM", label: "Gambia" },
    { value: "GE", label: "Georgia" },
    { value: "DE", label: "Germany" },
    { value: "GH", label: "Ghana" },
    { value: "GR", label: "Greece" },
    { value: "GD", label: "Grenada" },
    { value: "GT", label: "Guatemala" },
    { value: "GN", label: "Guinea" },
    { value: "GW", label: "Guinea-Bissau" },
    { value: "GY", label: "Guyana" },
    { value: "HT", label: "Haiti" },
    { value: "HN", label: "Honduras" },
    { value: "HK", label: "Hong Kong" },
    { value: "HU", label: "Hungary" },
    { value: "IS", label: "Iceland" },
    { value: "IN", label: "India" },
    { value: "ID", label: "Indonesia" },
    { value: "IR", label: "Iran" },
    { value: "IQ", label: "Iraq" },
    { value: "IE", label: "Ireland" },
    { value: "IL", label: "Israel" },
    { value: "IT", label: "Italy" },
    { value: "JM", label: "Jamaica" },
    { value: "JP", label: "Japan" },
    { value: "JO", label: "Jordan" },
    { value: "KZ", label: "Kazakhstan" },
    { value: "KE", label: "Kenya" },
    { value: "KI", label: "Kiribati" },
    { value: "XK", label: "Kosovo" },
    { value: "KW", label: "Kuwait" },
    { value: "KG", label: "Kyrgyzstan" },
    { value: "LA", label: "Laos" },
    { value: "LV", label: "Latvia" },
    { value: "LB", label: "Lebanon" },
    { value: "LS", label: "Lesotho" },
    { value: "LR", label: "Liberia" },
    { value: "LY", label: "Libya" },
    { value: "LI", label: "Liechtenstein" },
    { value: "LT", label: "Lithuania" },
    { value: "LU", label: "Luxembourg" },
    { value: "MO", label: "Macau" },
    { value: "MG", label: "Madagascar" },
    { value: "MW", label: "Malawi" },
    { value: "MY", label: "Malaysia" },
    { value: "MV", label: "Maldives" },
    { value: "ML", label: "Mali" },
    { value: "MT", label: "Malta" },
    { value: "MH", label: "Marshall Islands" },
    { value: "MR", label: "Mauritania" },
    { value: "MU", label: "Mauritius" },
    { value: "MX", label: "Mexico" },
    { value: "FM", label: "Micronesia" },
    { value: "MD", label: "Moldova" },
    { value: "MC", label: "Monaco" },
    { value: "MN", label: "Mongolia" },
    { value: "ME", label: "Montenegro" },
    { value: "MA", label: "Morocco" },
    { value: "MZ", label: "Mozambique" },
    { value: "MM", label: "Myanmar" },
    { value: "NA", label: "Namibia" },
    { value: "NR", label: "Nauru" },
    { value: "NP", label: "Nepal" },
    { value: "NL", label: "Netherlands" },
    { value: "NZ", label: "New Zealand" },
    { value: "NI", label: "Nicaragua" },
    { value: "NE", label: "Niger" },
    { value: "NG", label: "Nigeria" },
    { value: "KP", label: "North Korea" },
    { value: "MK", label: "North Macedonia" },
    { value: "NO", label: "Norway" },
    { value: "OM", label: "Oman" },
    { value: "PK", label: "Pakistan" },
    { value: "PW", label: "Palau" },
    { value: "PS", label: "Palestine" },
    { value: "PA", label: "Panama" },
    { value: "PG", label: "Papua New Guinea" },
    { value: "PY", label: "Paraguay" },
    { value: "PE", label: "Peru" },
    { value: "PH", label: "Philippines" },
    { value: "PL", label: "Poland" },
    { value: "PT", label: "Portugal" },
    { value: "PR", label: "Puerto Rico" },
    { value: "QA", label: "Qatar" },
    { value: "CG", label: "Republic of the Congo" },
    { value: "RO", label: "Romania" },
    { value: "RU", label: "Russia" },
    { value: "RW", label: "Rwanda" },
    { value: "KN", label: "Saint Kitts and Nevis" },
    { value: "LC", label: "Saint Lucia" },
    { value: "VC", label: "Saint Vincent and the Grenadines" },
    { value: "WS", label: "Samoa" },
    { value: "SM", label: "San Marino" },
    { value: "ST", label: "São Tomé and Príncipe" },
    { value: "SA", label: "Saudi Arabia" },
    { value: "SN", label: "Senegal" },
    { value: "RS", label: "Serbia" },
    { value: "SC", label: "Seychelles" },
    { value: "SL", label: "Sierra Leone" },
    { value: "SG", label: "Singapore" },
    { value: "SK", label: "Slovakia" },
    { value: "SI", label: "Slovenia" },
    { value: "SB", label: "Solomon Islands" },
    { value: "SO", label: "Somalia" },
    { value: "ZA", label: "South Africa" },
    { value: "KR", label: "South Korea" },
    { value: "SS", label: "South Sudan" },
    { value: "ES", label: "Spain" },
    { value: "LK", label: "Sri Lanka" },
    { value: "SD", label: "Sudan" },
    { value: "SR", label: "Suriname" },
    { value: "SE", label: "Sweden" },
    { value: "CH", label: "Switzerland" },
    { value: "SY", label: "Syria" },
    { value: "TW", label: "Taiwan" },
    { value: "TJ", label: "Tajikistan" },
    { value: "TZ", label: "Tanzania" },
    { value: "TH", label: "Thailand" },
    { value: "TL", label: "Timor-Leste" },
    { value: "TG", label: "Togo" },
    { value: "TO", label: "Tonga" },
    { value: "TT", label: "Trinidad and Tobago" },
    { value: "TN", label: "Tunisia" },
    { value: "TR", label: "Turkey" },
    { value: "TM", label: "Turkmenistan" },
    { value: "TV", label: "Tuvalu" },
    { value: "UG", label: "Uganda" },
    { value: "UA", label: "Ukraine" },
    { value: "AE", label: "United Arab Emirates" },
    { value: "GB", label: "United Kingdom" },
    { value: "US", label: "United States" },
    { value: "UY", label: "Uruguay" },
    { value: "UZ", label: "Uzbekistan" },
    { value: "VU", label: "Vanuatu" },
    { value: "VA", label: "Vatican City" },
    { value: "VE", label: "Venezuela" },
    { value: "VN", label: "Vietnam" },
    { value: "YE", label: "Yemen" },
    { value: "ZM", label: "Zambia" },
    { value: "ZW", label: "Zimbabwe" },
];

/**
 * Get state options based on selected country
 * @param {string} countryCode - Two-letter country code (e.g., "US", "CA")
 * @returns {Array} Array of state/province options for the given country
 */
export const getStateOptions = (countryCode) => {
    if (["US", "USA", "United States"].map(val => val.toLowerCase()).includes(countryCode?.toLowerCase())) {
        countryCode = 'US';
    }
    return countryStateMap[countryCode] || [];
};

/**
 * Filter country options based on search term
 * @param {string} searchTerm - Search string to filter countries
 * @returns {Array} Filtered array of country options
 */
export const getFilteredCountryOptions = (searchTerm) => {
    if (!searchTerm) return countryOptions;
    return countryOptions.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
};

export const getCountryByValue = (value) => {
    if (value == null || value === '') return '';
    if (["US", "USA", "United States"].map(val => val.toLowerCase()).includes(value.toLowerCase())) {
        value = 'US';
    }
    return countryOptions.find(option =>
        option.value.toLowerCase().includes(String(value).toLowerCase())
    )?.label || value;
};

/**
 * Filter state options based on country and search term
 * @param {string} countryCode - Two-letter country code
 * @param {string} searchTerm - Search string to filter states
 * @returns {Array} Filtered array of state/province options
 */
export const getFilteredStateOptions = (countryCode, searchTerm) => {
    const stateOptions = getStateOptions(countryCode);
    if (!searchTerm) return stateOptions;
    return stateOptions.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
};

export const parseAddressComponents = (place) => {
    const components = place.address_components || [];
    let streetNumber = "";
    let route = "";
    let city = "";
    let state = "";
    let country = "";
    let countryCode = "";

    components.forEach((component) => {
        const types = component.types;

        if (types.includes("street_number")) {
            streetNumber = component.long_name;
        } else if (types.includes("route")) {
            route = component.long_name;
        } else if (types.includes("locality")) {
            city = component.long_name;
        } else if (types.includes("administrative_area_level_1")) {
            state = component.short_name; // e.g., "CA", "NY", "ON"
        } else if (types.includes("country")) {
            country = component.long_name; // e.g., "United States"
            countryCode = component.short_name; // e.g., "US", "CA"
        }
    });

    const streetAddress = `${streetNumber} ${route}`.trim() || place?.name;

    return { streetAddress, city, state, country, countryCode };
};